"use client";

import { Fragment, useState, useEffect } from "react";
import { supabase } from "@/supabase/client";
import { useAuth } from "@/contexts/authContext";
import {
  Ticket,
  RefreshCw,
  Check,
  X,
  Coins,
  BarChart3,
  Percent,
  ImageIcon,
  Clock,
  Trophy,
  Users,
  Zap,
  History,
  Sparkles,
  Diamond,
  HandCoins,
  CheckCircle,
  XCircle,
  ExternalLink,
} from "lucide-react";
import Badge from "@/components/ui/badge";
import { ethers } from "ethers";
import { rewardAbi } from "@/components/abi/nodes";
import erc20Abi from "@/components/abi/erc20ABI.json";
import Modal from "@/components/ui/Modal";
import { toast } from "react-toastify";
// @ts-expect-error - jsonwebtoken types issue
import jwt from "jsonwebtoken";
import ShortUniqueId from "short-unique-id";

type TabType = "active" | "past" | "upcoming";
type EntryMethod = "gold" | "nft" | "purchased";

interface SupabaseRaffle {
  id: string;
  name: string;
  schedule_type: string;
  start_date: string;
  end_date: string;
  total_prize_pool: number;
  status: "active" | "upcoming" | "past";
  test?: boolean;
  created_at: string;
  prize_distribution_algorithm: string;
  first_place_percentage: number;
  ticket_price: number;
  entry_sources: unknown;
  winners_selected: boolean;
}

interface RaffleEntry {
  id: string;
  raffle_id: string;
  total_power: number;
  entries: number;
  created_at: string;
  purchased_entries: number;
  nft_entries: number;
  gold_entries: number;
  purchased_count: number;
  gold_count: number;
  nft_count: number;
  wallet_address: string;
  wallet_address_lc: string;
  user_id: string;
}

interface RaffleWinner {
  id: string;
  raffle_id: string;
  prize_amount: number;
  claimed: boolean;
  claimed_at: string | null;
  wallet_address: string;
  wallet_address_lc: string;
  transaction_hash: string | null;
  created_at: string;
  round_id: string;
}

interface RafflePurchaseAttempt {
  attempt_id: string;
  raffle_id: string;
  user_id: string | null;
  wallet_address: string;
  wallet_address_lc: string;
  ticket_count: number;
  ticket_price: number;
  total_price: number;
  token_symbol: string;
  token_address: string;
  receiver_address: string;
  chain_id: number;
  chain_name: string;
  status:
    | "initiated"
    | "submitted"
    | "confirmed"
    | "db_recorded"
    | "failed"
    | "cancelled"
    | "needs_reconciliation";
  tx_hash: string | null;
  error_message: string | null;
  metadata: Record<string, unknown>;
  submitted_at: string | null;
  confirmed_at: string | null;
  db_recorded_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Raffle {
  id: string;
  name: string;
  schedule_type: string;
  start_date: string;
  end_date: string;
  prizePool: number; // Renamed from total_prize_pool
  status: "active" | "upcoming" | "past";
  created_at: string;
  prize_distribution_algorithm: string;
  first_place_percentage: number;
  ticket_price: number;
  entry_sources: unknown;
  winners_selected: boolean;
  participants?: number; // Added for active raffles
  isEligibleToClaim?: boolean; // Added for active raffles
  claimAmount?: number; // Added for active raffles
  userEntries?: number; // Added for active raffles
  winner?: string; // Added for past raffles
  won?: boolean; // Added for past raffles
  wonAmount?: number; // Added for past raffles
  startsIn?: string; // Added for upcoming raffles
  entryFee?: number; // Added for upcoming raffles
  winnerInfo?: RaffleWinner; // Added for winner information
}

const CONTRACT_ADDRESS_FOR_RAFFLE_REWARD_CLAIM =
  "0x6BD5Fa6740654796bA2fd68D5c24957595679A1E";
const XSEED_TOKEN_ADDRESS = "0x0A10D108E2d81ccC793E37b56206C84bF96ddc57";
const XSEED_RECEIVER_ADDRESS = "0x23a5d161472d953065D3670D19F9029910862a76";
const XSEED_TOKEN_SYMBOL = "XSEED";
const XSEED_TOKEN_DECIMALS = 18;
const XSEED_TOKEN_IMAGE =
  "https://res.cloudinary.com/dot2onqdb/image/upload/v1729067770/xseed_lr_is7mcx.png";
const AVALANCHE_CHAIN_ID = "0xa86a";
const AVALANCHE_NETWORK_PARAMS = {
  chainId: AVALANCHE_CHAIN_ID,
  chainName: "Avalanche C-Chain",
  nativeCurrency: {
    name: "AVAX",
    symbol: "AVAX",
    decimals: 18,
  },
  rpcUrls: ["https://api.avax.network/ext/bc/C/rpc"],
  blockExplorerUrls: ["https://snowtrace.io/"],
};
const SNOWTRACE_TX_BASE_URL = "https://snowtrace.io/tx/";
const PURCHASE_HISTORY_PAGE_SIZE = 2;

function GamifiedRaffle() {
  const [activeTab, setActiveTab] = useState<TabType>("active");
  const [entryMethod, setEntryMethod] = useState<EntryMethod>("nft");
  const [ticketCount, setTicketCount] = useState(1);

  const [activeRaffles, setActiveRaffles] = useState<Raffle[]>([]);
  const [pastRaffles, setPastRaffles] = useState<Raffle[]>([]);
  const [upcomingRaffles, setUpcomingRaffles] = useState<Raffle[]>([]);
  const [loadingRaffles, setLoadingRaffles] = useState(true);
  const [errorRaffles, setErrorRaffles] = useState<string | null>(null);
  const [raffleEntries, setRaffleEntries] = useState<Map<string, RaffleEntry>>(
    new Map(),
  );
  const [rafflesLoaded, setRafflesLoaded] = useState(false);
  const [claimLoading, setClaimLoading] = useState<Record<string, boolean>>({});
  const [claimStatusLoading, setClaimStatusLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState<
    Record<string, boolean>
  >({});
  const [addTokenLoading, setAddTokenLoading] = useState(false);
  const [purchaseAttemptsByRaffle, setPurchaseAttemptsByRaffle] = useState<
    Map<string, RafflePurchaseAttempt[]>
  >(new Map());
  const [purchaseHistoryPageByRaffle, setPurchaseHistoryPageByRaffle] =
    useState<Map<string, number>>(new Map());
  const [purchaseModalRaffle, setPurchaseModalRaffle] = useState<Raffle | null>(
    null,
  );
  const [selectedPurchaseAttempt, setSelectedPurchaseAttempt] =
    useState<RafflePurchaseAttempt | null>(null);
  const [unclaimedRewardsCount, setUnclaimedRewardsCount] = useState(0);
  const [prefetchedPastWinners, setPrefetchedPastWinners] = useState(false);
  interface HistoryItem {
    id: string;
    name: string;
    date: string;
    won: boolean;
    amount: number;
    claimed: boolean;
  }

  const [dynamicHistory, setDynamicHistory] = useState<HistoryItem[]>([]);
  const {
    user,
    walletAddress,
    isAuthenticated,
    isFetchingUser,
    ipAddress,
    openLoginModal,
  } = useAuth();

  const formatPastRaffleDate = (dateString: string) => {
    const date = new Date(dateString);

    return {
      month: date.toLocaleString("en-US", { month: "short" }),
      day: date.toLocaleString("en-US", { day: "numeric" }),
      year: date.toLocaleString("en-US", { year: "numeric" }),
    };
  };

  const getPastRaffleSubtitle = (raffle: Raffle) => {
    if (!raffle.schedule_type) {
      return "Community raffle rewards.";
    }

    return `${
      raffle.schedule_type.charAt(0).toUpperCase() +
      raffle.schedule_type.slice(1)
    } raffle rewards.`;
  };

  const getPastRaffleStatus = (raffle: Raffle) => {
    if (raffle.winnerInfo?.claimed) {
      return {
        tone: "claimed" as const,
        title: "Congratulations! You won this raffle",
      };
    }

    if (raffle.winnerInfo) {
      return {
        tone: "unclaimed" as const,
        title: "Congratulations! You won this raffle",
      };
    }

    return {
      tone: "lost" as const,
      title: "You didn't win this time",
    };
  };

  const getUpcomingRaffleCountdown = (raffle: Raffle) => {
    const now = Date.now();
    const startMs = new Date(raffle.start_date).getTime();
    const createdMs = new Date(raffle.created_at).getTime();
    const remainingMs = Math.max(0, startMs - now);
    const totalWindowMs = Math.max(startMs - createdMs, 1);
    const elapsedMs = Math.min(totalWindowMs, Math.max(0, now - createdMs));
    const progress = Math.max(
      0,
      Math.min(100, Math.round((elapsedMs / totalWindowMs) * 100)),
    );

    const totalMinutes = Math.floor(remainingMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    return {
      days: String(days),
      hours: String(hours).padStart(2, "0"),
      minutes: String(minutes).padStart(2, "0"),
      progress,
    };
  };

  const getUpcomingRaffleSubtitle = (raffle: Raffle) => {
    if (!raffle.schedule_type) {
      return "Enter for a chance to win exclusive rewards.";
    }

    return `Enter for a chance to win exclusive ${raffle.schedule_type} prizes and bonus rewards.`;
  };

  const renderEmptyState = ({
    icon,
    title,
    description,
  }: {
    icon: JSX.Element;
    title: string;
    description: string;
  }) => (
    <div className="rounded-2xl border border-white/10 bg-[#131416] px-6 py-10 text-center shadow-[0_18px_50px_rgba(0,0,0,0.24)]">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-[#B6B8BD]">
        {icon}
      </div>
      <h3 className="mt-5 text-lg font-semibold text-white">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#9DA3AE]">
        {description}
      </p>
    </div>
  );

  const suid = new ShortUniqueId({ length: 28 });

  // Helper to generate JWT token
  const generateToken = (
    address: string,
    payload: Record<string, unknown> = {},
  ) => {
    const secret =
      "mxs-games-to-the-moon-and-back-and-to-pluto-and-back-and-to-jupiter-and-back-we-got-this";
    const expiresIn = "1h";
    if (!address) throw new Error("Wallet address is required");
    if (!secret) throw new Error("Secret key is required");
    const basePayload = { walletAddress: address };
    const tokenPayload = { ...basePayload, ...payload };
    try {
      return jwt.sign(tokenPayload, secret, { expiresIn });
    } catch {
      throw new Error("Failed to generate JWT token");
    }
  };

  // Helper to get user IP address
  const getIpAddress = async (): Promise<string> => {
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      const data = await res.json();
      return data.ip;
    } catch (err) {
      console.error("Failed to fetch IP address:", err);
      return ipAddress || "unknown";
    }
  };

  // Helper to log to sys_audit
  const logSysAudit = async ({
    uid,
    email,
    ip_address,
    event = "XSEED",
    sub_event = "REWARD",
    type = "RAFFLE",
    game_name = "ECOSYSTEM",
    success,
    comment,
    target = "",
  }: {
    uid: string | undefined;
    email: string | undefined;
    ip_address: string;
    event?: string;
    sub_event?: string;
    type?: string;
    game_name?: string;
    success: boolean;
    comment: string;
    target: string;
  }) => {
    try {
      const id = suid.rnd();
      const { data, error } = await supabase.from("sys_audit").insert([
        {
          id,
          uid,
          email,
          ip_address,
          event,
          sub_event,
          type,
          game_name,
          success,
          comment,
          target,
        },
      ]);
      if (error) {
        console.error("Error inserting audit log:", error);
        throw error;
      }
      return data;
    } catch (err) {
      console.error("Failed to insert audit log:", err);
      throw err;
    }
  };

  // Fetch raffles on mount - always fetch regardless of auth status
  useEffect(() => {
    const fetchRaffles = async () => {
      setLoadingRaffles(true);
      setErrorRaffles(null);
      try {
        const { data: fetchedRaffles, error } = (await supabase
          .from("raffles")
          .select("*")
          .order("created_at", { ascending: false })) as {
          data: SupabaseRaffle[] | null;
          error: import("@supabase/supabase-js").PostgrestError | null;
        };

        if (error) {
          console.error("Error fetching raffles:", error);
          throw error;
        }

        if (!fetchedRaffles || fetchedRaffles.length === 0) {
          setActiveRaffles([]);
          setPastRaffles([]);
          setUpcomingRaffles([]);
          setActiveTab("upcoming");
          setLoadingRaffles(false);
          return;
        }

        const now = new Date();
        const active: Raffle[] = [];
        const past: Raffle[] = [];
        const upcoming: Raffle[] = [];

        fetchedRaffles.forEach((raffle: SupabaseRaffle) => {
          if (raffle.test === true) {
            console.log("→ Skipping TEST raffle:", raffle.name);
            return;
          }

          const transformedRaffle: Raffle = {
            ...raffle,
            prizePool: raffle.total_prize_pool,
            userEntries: 0, // Will be updated when user entries are fetched
          };

          const startDate = new Date(transformedRaffle.start_date);
          const endDate = new Date(transformedRaffle.end_date);

          // Debug logging
          console.log("Raffle:", raffle.name, {
            status: raffle.status,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            now: now.toISOString(),
            isActive: now >= startDate && now <= endDate,
          });

          // Categorize raffles based on dates and status
          // Prioritize date checks, but also respect status field
          if (now >= startDate && now <= endDate) {
            // Currently within the date range - consider it active
            active.push(transformedRaffle);
            console.log("→ Added to ACTIVE:", raffle.name);
          } else if (now > endDate || transformedRaffle.status === "past") {
            // Past the end date or marked as past
            past.push(transformedRaffle);
            console.log("→ Added to PAST:", raffle.name);
          } else if (
            now < startDate ||
            transformedRaffle.status === "upcoming"
          ) {
            // Before start date or marked as upcoming
            upcoming.push(transformedRaffle);
            console.log("→ Added to UPCOMING:", raffle.name);
          } else if (transformedRaffle.status === "active") {
            // Fallback: if status is explicitly active, include it
            active.push(transformedRaffle);
            console.log("→ Added to ACTIVE (by status):", raffle.name);
          } else {
            // Default fallback - if no category matches, check status
            if (transformedRaffle.status === "active") {
              active.push(transformedRaffle);
            } else if (transformedRaffle.status === "past") {
              past.push(transformedRaffle);
            } else {
              upcoming.push(transformedRaffle);
            }
            console.log(
              "→ Added to",
              transformedRaffle.status || "upcoming",
              ":",
              raffle.name,
            );
          }
        });

        console.log("Categorized raffles:", {
          active: active.length,
          past: past.length,
          upcoming: upcoming.length,
        });

        setActiveRaffles(active);
        // Sort past raffles: unclaimed first, then by end date (newest first)
        past.sort((a, b) => {
          const aUnclaimed = !!a.winnerInfo && !a.winnerInfo.claimed;
          const bUnclaimed = !!b.winnerInfo && !b.winnerInfo.claimed;
          if (aUnclaimed !== bUnclaimed) {
            return aUnclaimed ? -1 : 1;
          }
          return (
            new Date(b.end_date).getTime() - new Date(a.end_date).getTime()
          );
        });
        setPastRaffles(past);
        setUpcomingRaffles(upcoming);
        setActiveTab(
          active.length > 0
            ? "active"
            : upcoming.length > 0
              ? "upcoming"
              : "past",
        );
        setRafflesLoaded(true);
      } catch (error) {
        console.error("Error fetching raffles:", error);
        setErrorRaffles("Failed to fetch raffles.");
        setRafflesLoaded(true); // Still mark as loaded even on error
      } finally {
        setLoadingRaffles(false);
      }
    };

    fetchRaffles();
  }, []);

  useEffect(() => {
    if (!rafflesLoaded) return;

    const historyItems: HistoryItem[] = [];
    let unclaimedCount = 0;

    // Process past raffles
    pastRaffles.forEach((raffle) => {
      const winnerInfo = raffle.winnerInfo;
      const isUnclaimed = !!winnerInfo && !winnerInfo?.claimed;
      if (isUnclaimed) {
        unclaimedCount++;
      }
      historyItems.push({
        id: raffle.id,
        name: raffle.name,
        date: new Date(raffle.end_date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        won: !!winnerInfo,
        amount: winnerInfo ? winnerInfo.prize_amount : 0,
        claimed: !!winnerInfo?.claimed, // Add claimed status
      });
    });

    // Process active raffles that have winners selected
    activeRaffles.forEach((raffle) => {
      if (raffle.winners_selected) {
        const winnerInfo = raffle.winnerInfo;
        historyItems.push({
          id: raffle.id,
          name: raffle.name,
          date: new Date(raffle.end_date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          won: !!winnerInfo,
          amount: winnerInfo ? winnerInfo.prize_amount : 0,
          claimed: !!winnerInfo?.claimed, // Add claimed status
        });
      }
    });

    setDynamicHistory(
      historyItems.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      ),
    );
    setUnclaimedRewardsCount(unclaimedCount);
  }, [pastRaffles, activeRaffles, rafflesLoaded]);

  useEffect(() => {
    const prefetchPastWinners = async () => {
      if (prefetchedPastWinners) return;
      if (!rafflesLoaded || !user || !walletAddress) return;
      if (!pastRaffles || pastRaffles.length === 0) return;
      const pastIds = pastRaffles.map((r) => r.id);
      const walletAddressLower = walletAddress.toLowerCase();
      try {
        const { data: raffleWinners, error: winnersError } = (await supabase
          .from("raffle_winners")
          .select("*")
          .in("raffle_id", pastIds)
          .or(
            `wallet_address.eq.${walletAddress},wallet_address_lc.eq.${walletAddressLower}`,
          )) as {
          data: RaffleWinner[] | null;
          error: unknown;
        };
        if (winnersError) {
          console.error("❌ Prefetch past winners error:", winnersError);
          return;
        }
        const winnersMap = new Map<string, RaffleWinner>();
        raffleWinners?.forEach((w) => winnersMap.set(w.raffle_id, w));
        setPastRaffles((prev) =>
          prev.map((r) => ({
            ...r,
            winnerInfo: winnersMap.get(r.id) ?? r.winnerInfo,
          })),
        );
        setPrefetchedPastWinners(true);
      } catch (e) {
        console.error("❌ Prefetch past winners exception:", e);
      }
    };
    prefetchPastWinners();
  }, [
    rafflesLoaded,
    user,
    walletAddress,
    pastRaffles.length,
    prefetchedPastWinners,
  ]);

  useEffect(() => {
    const fetchPurchaseAttempts = async () => {
      if (!rafflesLoaded || !walletAddress || !isAuthenticated || !user) {
        setPurchaseAttemptsByRaffle(new Map());
        return;
      }

      const raffleIds = [
        ...activeRaffles.map((raffle) => raffle.id),
        ...pastRaffles.map((raffle) => raffle.id),
        ...upcomingRaffles.map((raffle) => raffle.id),
      ];

      if (raffleIds.length === 0) {
        setPurchaseAttemptsByRaffle(new Map());
        return;
      }

      const { data, error } = (await supabase
        .from("raffle_purchase_attempts")
        .select("*")
        .in("raffle_id", raffleIds)
        .eq("wallet_address_lc", walletAddress.toLowerCase())
        .order("created_at", { ascending: false })) as {
        data: RafflePurchaseAttempt[] | null;
        error: import("@supabase/supabase-js").PostgrestError | null;
      };

      if (error) {
        console.error("Error fetching raffle purchase attempts:", error);
        return;
      }

      const attemptsMap = new Map<string, RafflePurchaseAttempt[]>();
      (data ?? []).forEach((attempt) => {
        const existing = attemptsMap.get(attempt.raffle_id) ?? [];
        existing.push(attempt);
        attemptsMap.set(attempt.raffle_id, existing);
      });

      setPurchaseAttemptsByRaffle(attemptsMap);
    };

    fetchPurchaseAttempts();
  }, [
    rafflesLoaded,
    walletAddress,
    isAuthenticated,
    user,
    activeRaffles,
    pastRaffles,
    upcomingRaffles,
  ]);

  // Fetch user entries when a tab is selected and user is authenticated
  useEffect(() => {
    const fetchUserEntries = async (): Promise<void> => {
      console.log("🔄 fetchUserEntries called for tab:", activeTab);
      // Only fetch if user is authenticated and we have a selected tab
      if (
        !isAuthenticated ||
        !user ||
        isFetchingUser ||
        !walletAddress ||
        !rafflesLoaded
      ) {
        return;
      }

      // Get raffle IDs based on the currently selected tab
      // Read current state values directly
      const currentActiveRaffles = activeRaffles;
      const currentPastRaffles = pastRaffles;
      const currentUpcomingRaffles = upcomingRaffles;

      // Log current state before switch
      console.log("🔍 Current state before switch:", {
        activeTab,
        activeRafflesCount: currentActiveRaffles.length,
        pastRafflesCount: currentPastRaffles.length,
        upcomingRafflesCount: currentUpcomingRaffles.length,
        activeRafflesSample: currentActiveRaffles
          .slice(0, 2)
          .map((r) => ({ id: r.id, name: r.name })),
        pastRafflesSample: currentPastRaffles
          .slice(0, 2)
          .map((r) => ({ id: r.id, name: r.name })),
        upcomingRafflesSample: currentUpcomingRaffles
          .slice(0, 2)
          .map((r) => ({ id: r.id, name: r.name })),
      });

      let raffleIds: string[] = [];
      let targetRaffles: Raffle[] = [];

      switch (activeTab) {
        case "active":
          targetRaffles = currentActiveRaffles;
          raffleIds = currentActiveRaffles.map((r) => r.id);
          console.log("📋 Active tab selected, raffle IDs:", raffleIds);
          break;
        case "past":
          targetRaffles = currentPastRaffles;
          raffleIds = currentPastRaffles.map((r) => r.id);
          console.log("📋 Past tab selected, raffle IDs:", raffleIds);
          break;
        case "upcoming":
          targetRaffles = currentUpcomingRaffles;
          raffleIds = currentUpcomingRaffles.map((r) => r.id);
          console.log("📋 Upcoming tab selected, raffle IDs:", raffleIds);
          break;
      }

      console.log("🔍 Processing raffles for tab:", activeTab, {
        raffleIds: raffleIds,
        raffleIdsCount: raffleIds.length,
        targetRafflesCount: targetRaffles.length,
        targetRafflesSample: targetRaffles
          .slice(0, 2)
          .map((r) => ({ id: r.id, name: r.name })),
      });

      if (raffleIds.length === 0) {
        console.log("⚠️ No raffle IDs found for the selected tab:", activeTab, {
          activeRaffles: activeRaffles.length,
          pastRaffles: pastRaffles.length,
          upcomingRaffles: upcomingRaffles.length,
        });
        // No raffles in the selected tab
        return;
      }

      const userId = user?.user_id || user?.uid;
      const walletAddressLower = walletAddress.toLowerCase();

      console.log("🔑 User Identification:", {
        userId: userId,
        walletAddress: walletAddress,
        walletAddressLower: walletAddressLower,
        hasUserId: !!userId,
        hasWalletAddress: !!walletAddress,
      });

      // Build query to fetch entries matching user AND raffle IDs for the selected tab
      // We MUST filter by both raffle_id AND user_id/wallet_address
      console.log("📊 Building Supabase query with filters:");
      console.log("  1. raffle_id IN:", raffleIds);
      console.log("  2. user_id:", userId || "NOT PROVIDED");
      console.log("  3. wallet_address:", walletAddress || "NOT PROVIDED");

      let query = supabase.from("raffle_entries").select("*");

      // STEP 1: Filter by raffle_ids for the selected tab (REQUIRED)
      query = query.in("raffle_id", raffleIds);
      console.log("✅ Added filter: raffle_id IN", raffleIds);

      // STEP 2: Filter by user_id (PREFERRED) or wallet_address (FALLBACK)
      // Supabase will AND these conditions: raffle_id IN (...) AND user_id = X
      if (userId) {
        query = query.eq("user_id", userId);
        console.log("✅ Added filter: user_id =", userId);
        console.log(
          "📝 Final query: raffle_id IN",
          raffleIds,
          "AND user_id =",
          userId,
        );
      } else if (walletAddressLower) {
        // Fallback to wallet_address if no user_id
        query = query.or(
          `wallet_address.eq.${walletAddress},wallet_address_lc.eq.${walletAddressLower}`,
        );
        console.log(
          "✅ Added filter: wallet_address =",
          walletAddress,
          "OR wallet_address_lc =",
          walletAddressLower,
        );
        console.log(
          "📝 Final query: raffle_id IN",
          raffleIds,
          "AND (wallet_address =",
          walletAddress,
          "OR wallet_address_lc =",
          walletAddressLower,
          ")",
        );
      } else {
        console.warn(
          "⚠️ WARNING: No user_id or wallet_address available for filtering!",
        );
      }

      console.log("🚀 Executing Supabase query...");
      const { data: userEntries, error: entriesError } = (await query) as {
        data: RaffleEntry[] | null;
        error: import("@supabase/supabase-js").PostgrestError | null;
      };

      if (entriesError) {
        console.error("❌ Error fetching user entries:", entriesError);
        console.error("   Error details:", {
          message: entriesError.message,
          details: entriesError.details,
          hint: entriesError.hint,
          code: entriesError.code,
        });
        return;
      }

      console.log("📦 Query Response:", {
        entriesCount: userEntries?.length || 0,
        hasEntries: !!(userEntries && userEntries.length > 0),
      });

      if (!userEntries || userEntries.length === 0) {
        console.log("ℹ️ No user entries found for this tab.");
        console.log("   Query parameters used:", {
          raffleIds: raffleIds,
          userId: userId,
          walletAddress: walletAddress,
        });
        console.log("   This means: No entries in raffle_entries table where");
        console.log("     - raffle_id IN", raffleIds);
        console.log(
          "     - AND user_id =",
          userId || "OR wallet_address =",
          walletAddress,
        );
        // No entries found for this user in the selected tab's raffles
        // Update the current entries map to clear entries for these raffles
        setRaffleEntries((prev) => {
          const newMap = new Map(prev);
          raffleIds.forEach((id) => {
            // Keep entries from other tabs, just ensure current tab has no entries
            if (!newMap.has(id)) {
              // Entry doesn't exist, that's fine
            }
          });
          return newMap;
        });
        return;
      }

      // Map entries by raffle_id
      console.log("📋 Processing fetched entries...");
      const entriesMap = new Map<string, RaffleEntry>();
      userEntries.forEach((entry, index) => {
        console.log(`   Entry ${index + 1}:`, {
          raffle_id: entry.raffle_id,
          user_id: entry.user_id,
          wallet_address: entry.wallet_address,
          entries: entry.entries,
          gold_count: entry.gold_count,
          gold_entries: entry.gold_entries,
          nft_count: entry.nft_count,
          nft_entries: entry.nft_entries,
          total_power: entry.total_power,
        });
        entriesMap.set(entry.raffle_id, entry);
      });
      console.log(
        "🗺️ Mapped user entries (entriesMap):",
        Array.from(entriesMap.entries()).map(([raffleId, entry]) => ({
          raffle_id: raffleId,
          user_id: entry.user_id,
          entries: entry.entries,
          gold_count: entry.gold_count,
          gold_entries: entry.gold_entries,
          nft_count: entry.nft_count,
          nft_entries: entry.nft_entries,
          total_power: entry.total_power,
        })),
      );

      // NEW: Fetch raffle_winners for the current user and raffles
      console.log("🏆 Fetching raffle winners for current user...");
      const { data: raffleWinners, error: winnersError } = (await supabase
        .from("raffle_winners")
        .select("*")
        .in("raffle_id", raffleIds)
        .or(
          `wallet_address.eq.${walletAddress},wallet_address_lc.eq.${walletAddressLower}`,
        )) as {
        data: RaffleWinner[] | null;
        error: import("@supabase/supabase-js").PostgrestError | null;
      };

      if (winnersError) {
        console.error("❌ Error fetching raffle winners:", winnersError);
        return;
      }

      const winnersMap = new Map<string, RaffleWinner>();
      raffleWinners?.forEach((winner) => {
        winnersMap.set(winner.raffle_id, winner);
      });
      console.log(
        "🗺️ Mapped raffle winners (winnersMap):",
        Array.from(winnersMap.entries()).map(([raffleId, winner]) => ({
          raffle_id: raffleId,
          claimed: winner.claimed,
          prize_amount: winner.prize_amount,
        })),
      );

      // Merge with existing entries map (keep entries from other tabs)
      setRaffleEntries((prev) => {
        const newMap = new Map(prev);
        entriesMap.forEach((value, key) => {
          newMap.set(key, value);
        });
        return newMap;
      });

      // Update only the raffles in the selected tab with user entry data and winner info
      console.log(
        "🔄 Updating raffles with user entry data and winner info for tab:",
        activeTab,
      );
      switch (activeTab) {
        case "active":
          setActiveRaffles((prev) => {
            const updatedRaffles = prev.map((raffle) => {
              const entry = entriesMap.get(raffle.id);
              const winnerInfo = winnersMap.get(raffle.id); // Get winner info
              console.log(`   Updating raffle ${raffle.id}:`, {
                raffleName: raffle.name,
                hasEntry: !!entry,
                userEntries: entry?.entries ?? 0,
                gold_count: entry?.gold_count ?? 0,
                gold_entries: entry?.gold_entries ?? 0,
                nft_count: entry?.nft_count ?? 0,
                nft_entries: entry?.nft_entries ?? 0,
                total_power: entry?.total_power ?? 0,
                winnerInfo: winnerInfo, // Add winner info
              });
              return {
                ...raffle,
                userEntries: entry?.entries ?? 0,
                winnerInfo: winnerInfo, // Add winner info
              };
            });
            console.log(
              "✅ Updated activeRaffles:",
              updatedRaffles.map((r) => ({
                id: r.id,
                name: r.name,
                userEntries: r.userEntries,
                winnerInfo: r.winnerInfo, // Log winner info
              })),
            );
            return updatedRaffles;
          });
          break;
        case "past":
          setPastRaffles((prev) => {
            const updatedRaffles = prev.map((raffle) => {
              const entry = entriesMap.get(raffle.id);
              const winnerInfo = winnersMap.get(raffle.id); // Get winner info
              console.log(`   Updating raffle ${raffle.id}:`, {
                raffleName: raffle.name,
                hasEntry: !!entry,
                userEntries: entry?.entries ?? 0,
                gold_count: entry?.gold_count ?? 0,
                gold_entries: entry?.gold_entries ?? 0,
                nft_count: entry?.nft_count ?? 0,
                nft_entries: entry?.nft_entries ?? 0,
                total_power: entry?.total_power ?? 0,
                winnerInfo: winnerInfo, // Add winner info
              });
              return {
                ...raffle,
                userEntries: entry?.entries ?? 0,
                winnerInfo: winnerInfo, // Add winner info
              };
            });
            console.log(
              "✅ Updated pastRaffles:",
              updatedRaffles.map((r) => ({
                id: r.id,
                name: r.name,
                userEntries: r.userEntries,
                winnerInfo: r.winnerInfo, // Log winner info
              })),
            );
            return updatedRaffles;
          });
          break;
        case "upcoming":
          setUpcomingRaffles((prev) => {
            const updatedRaffles = prev.map((raffle) => {
              const entry = entriesMap.get(raffle.id);
              const winnerInfo = winnersMap.get(raffle.id); // Get winner info
              console.log(`   Updating raffle ${raffle.id}:`, {
                raffleName: raffle.name,
                hasEntry: !!entry,
                userEntries: entry?.entries ?? 0,
                gold_count: entry?.gold_count ?? 0,
                gold_entries: entry?.gold_entries ?? 0,
                nft_count: entry?.nft_count ?? 0,
                nft_entries: entry?.nft_entries ?? 0,
                total_power: entry?.total_power ?? 0,
                winnerInfo: winnerInfo, // Add winner info
              });
              return {
                ...raffle,
                userEntries: entry?.entries ?? 0,
                winnerInfo: winnerInfo, // Add winner info
              };
            });
            console.log(
              "✅ Updated upcomingRaffles:",
              updatedRaffles.map((r) => ({
                id: r.id,
                name: r.name,
                userEntries: r.userEntries,
                winnerInfo: r.winnerInfo, // Log winner info
              })),
            );
            return updatedRaffles;
          });
          break;
      }

      console.log(
        "🎉 fetchUserEntries completed successfully for tab:",
        activeTab,
      );
    };

    fetchUserEntries();
  }, [
    activeTab,
    isAuthenticated,
    user,
    walletAddress,
    isFetchingUser,
    rafflesLoaded,
    activeRaffles.length, // Only track length to avoid infinite loops
    pastRaffles.length,
    upcomingRaffles.length,
  ]);

  // Stats - Calculate from all raffle entries
  const calculateStats = () => {
    let totalEntries = 0;
    let wins = 0;
    let totalWinnings = 0;

    // Calculate totalEntries from raffleEntries
    raffleEntries.forEach((entry) => {
      totalEntries += entry.entries || 0;
    });

    // Calculate wins and totalWinnings from dynamicHistory
    dynamicHistory.forEach((item) => {
      if (item.won) {
        wins += 1;
        totalWinnings += item.amount || 0;
      }
    });

    // Calculate winRate based on dynamicHistory length
    const totalRafflesParticipated = dynamicHistory.length;
    const winRate =
      totalRafflesParticipated > 0
        ? Math.round((wins / totalRafflesParticipated) * 100)
        : 0;

    return {
      totalEntries,
      wins,
      winRate,
      totalWinnings,
    };
  };

  const stats = calculateStats();
  const purchaseTicketCount = Math.max(1, ticketCount);
  const purchaseTotalCost = purchaseModalRaffle
    ? (purchaseModalRaffle.ticket_price || 0) * purchaseTicketCount
    : 0;
  const purchaseSuccessStatuses: RafflePurchaseAttempt["status"][] = [
    "submitted",
    "confirmed",
    "db_recorded",
    "needs_reconciliation",
  ];

  const getPurchaseStatusTone = (status: RafflePurchaseAttempt["status"]) => {
    switch (status) {
      case "db_recorded":
        return "bg-green-500/15 text-green-300 border-green-500/30";
      case "confirmed":
        return "bg-blue-500/15 text-blue-300 border-blue-500/30";
      case "submitted":
        return "bg-amber-500/15 text-amber-300 border-amber-500/30";
      case "needs_reconciliation":
        return "bg-orange-500/15 text-orange-300 border-orange-500/30";
      case "cancelled":
        return "bg-gray-500/15 text-gray-300 border-gray-500/30";
      case "failed":
        return "bg-red-500/15 text-red-300 border-red-500/30";
      default:
        return "bg-white/10 text-gray-300 border-white/10";
    }
  };

  const formatAttemptDate = (value?: string | null) => {
    if (!value) return "Pending";
    return new Date(value).toLocaleString();
  };

  const getPurchaseHistoryPage = (raffleId: string) =>
    purchaseHistoryPageByRaffle.get(raffleId) ?? 1;

  const setPurchaseHistoryPage = (raffleId: string, page: number) => {
    setPurchaseHistoryPageByRaffle((prev) => {
      const next = new Map(prev);
      next.set(raffleId, page);
      return next;
    });
  };

  const openPurchaseModal = (raffle: Raffle) => {
    setPurchaseModalRaffle(raffle);
  };

  const closePurchaseModal = () => {
    if (purchaseModalRaffle && purchaseLoading[purchaseModalRaffle.id]) {
      return;
    }

    setPurchaseModalRaffle(null);
  };

  const switchToAvalanche = async (ethereumProvider: NonNullable<typeof window.ethereum>) => {
    const currentChainId = await ethereumProvider.request({
      method: "eth_chainId",
    });

    if (currentChainId === AVALANCHE_CHAIN_ID) {
      return;
    }

    try {
      await ethereumProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: AVALANCHE_CHAIN_ID }],
      });
    } catch (error: unknown) {
      if (
        error &&
        typeof error === "object" &&
        "code" in error &&
        Number((error as { code?: number }).code) === 4902
      ) {
        await ethereumProvider.request({
          method: "wallet_addEthereumChain",
          params: [AVALANCHE_NETWORK_PARAMS],
        });
        return;
      }

      throw error;
    }
  };

  const connectMetamaskForXseed = async () => {
    if (typeof window === "undefined" || !window.ethereum) {
      throw new Error("MetaMask is not installed.");
    }

    const ethereumProvider = window.ethereum;
    const accounts = (await ethereumProvider.request({
      method: "eth_requestAccounts",
    })) as string[];
    const activeAccount = accounts?.[0];

    if (!activeAccount) {
      throw new Error("No MetaMask account found.");
    }

    if (
      walletAddress &&
      activeAccount.toLowerCase() !== walletAddress.toLowerCase()
    ) {
      throw new Error(
        "Connected account doesn't match the logged-in wallet. Please switch MetaMask to the same wallet first.",
      );
    }

    await switchToAvalanche(ethereumProvider);

    const provider = new ethers.providers.Web3Provider(ethereumProvider, "any");
    const signer = provider.getSigner();
    const tokenContract = new ethers.Contract(
      XSEED_TOKEN_ADDRESS,
      erc20Abi,
      signer,
    );

    return {
      ethereumProvider,
      provider,
      signer,
      tokenContract,
      activeAccount,
    };
  };

  const syncPurchasedTicketsLocally = (
    raffleId: string,
    purchaseCount: number,
    updatedEntry?: RaffleEntry,
  ) => {
    setRaffleEntries((prev) => {
      const next = new Map(prev);
      const existingEntry = prev.get(raffleId);
      const mergedEntry: RaffleEntry =
        updatedEntry ??
        ({
          ...(existingEntry ?? {
            id: suid.rnd(),
            raffle_id: raffleId,
            created_at: new Date().toISOString(),
            wallet_address: walletAddress ?? "",
            wallet_address_lc: walletAddress?.toLowerCase() ?? "",
            user_id: user?.user_id || user?.uid || "",
            total_power: 0,
            gold_entries: 0,
            nft_entries: 0,
            purchased_entries: 0,
            gold_count: 0,
            nft_count: 0,
            purchased_count: 0,
            entries: 0,
          }),
          entries: (existingEntry?.entries ?? 0) + purchaseCount,
          purchased_entries:
            (existingEntry?.purchased_entries ?? 0) + purchaseCount,
          purchased_count: (existingEntry?.purchased_count ?? 0) + purchaseCount,
        } as RaffleEntry);

      next.set(raffleId, mergedEntry);
      return next;
    });

    setActiveRaffles((prev) =>
      prev.map((raffle) =>
        raffle.id === raffleId
          ? {
              ...raffle,
              userEntries: (raffle.userEntries ?? 0) + purchaseCount,
            }
          : raffle,
      ),
    );
  };

  const upsertPurchasedTickets = async (
    raffle: Raffle,
    purchaseCount: number,
  ) => {
    const userId = user?.user_id || user?.uid;
    const walletAddressLower = walletAddress?.toLowerCase();

    if (!walletAddress || !walletAddressLower || !userId) {
      throw new Error("Missing wallet or user information.");
    }

    const { data: existingEntry, error: fetchError } = (await supabase
      .from("raffle_entries")
      .select("*")
      .eq("raffle_id", raffle.id)
      .eq("wallet_address_lc", walletAddressLower)
      .maybeSingle()) as {
      data: RaffleEntry | null;
      error: import("@supabase/supabase-js").PostgrestError | null;
    };

    if (fetchError) {
      throw fetchError;
    }

    if (existingEntry) {
      const { data, error } = (await supabase
        .from("raffle_entries")
        .update({
          entries: (existingEntry.entries ?? 0) + purchaseCount,
          purchased_entries:
            (existingEntry.purchased_entries ?? 0) + purchaseCount,
          purchased_count: (existingEntry.purchased_count ?? 0) + purchaseCount,
          wallet_address: walletAddress,
          wallet_address_lc: walletAddressLower,
          user_id: userId,
        })
        .eq("id", existingEntry.id)
        .select()
        .single()) as {
        data: RaffleEntry | null;
        error: import("@supabase/supabase-js").PostgrestError | null;
      };

      if (error) {
        throw error;
      }

      return data;
    }

    const { data, error } = (await supabase
      .from("raffle_entries")
      .insert([
        {
          id: suid.rnd(),
          raffle_id: raffle.id,
          user_id: userId,
          wallet_address: walletAddress,
          wallet_address_lc: walletAddressLower,
          entries: purchaseCount,
          purchased_entries: purchaseCount,
          purchased_count: purchaseCount,
          gold_entries: 0,
          gold_count: 0,
          nft_entries: 0,
          nft_count: 0,
          total_power: 0,
        },
      ])
      .select()
      .single()) as {
      data: RaffleEntry | null;
      error: import("@supabase/supabase-js").PostgrestError | null;
    };

    if (error) {
      throw error;
    }

    return data;
  };

  const createRafflePurchaseAttempt = async ({
    raffle,
    purchaseCount,
    totalCost,
  }: {
    raffle: Raffle;
    purchaseCount: number;
    totalCost: number;
  }) => {
    const attemptId = suid.rnd();
    const userId = user?.user_id || user?.uid || null;

    const { error } = await supabase.from("raffle_purchase_attempts").insert([
      {
        attempt_id: attemptId,
        raffle_id: raffle.id,
        user_id: userId,
        wallet_address: walletAddress,
        wallet_address_lc: walletAddress?.toLowerCase(),
        ticket_count: purchaseCount,
        ticket_price: raffle.ticket_price || 0,
        total_price: totalCost,
        token_symbol: XSEED_TOKEN_SYMBOL,
        token_address: XSEED_TOKEN_ADDRESS,
        receiver_address: XSEED_RECEIVER_ADDRESS,
        chain_id: 43114,
        chain_name: "Avalanche C-Chain",
        status: "initiated",
        metadata: {
          raffle_name: raffle.name,
          user_email: user?.email ?? null,
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    ]);

    if (error) {
      throw error;
    }

    const newAttempt: RafflePurchaseAttempt = {
      attempt_id: attemptId,
      raffle_id: raffle.id,
      user_id: userId,
      wallet_address: walletAddress ?? "",
      wallet_address_lc: walletAddress?.toLowerCase() ?? "",
      ticket_count: purchaseCount,
      ticket_price: raffle.ticket_price || 0,
      total_price: totalCost,
      token_symbol: XSEED_TOKEN_SYMBOL,
      token_address: XSEED_TOKEN_ADDRESS,
      receiver_address: XSEED_RECEIVER_ADDRESS,
      chain_id: 43114,
      chain_name: "Avalanche C-Chain",
      status: "initiated",
      tx_hash: null,
      error_message: null,
      metadata: {
        raffle_name: raffle.name,
        user_email: user?.email ?? null,
      },
      submitted_at: null,
      confirmed_at: null,
      db_recorded_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    setPurchaseAttemptsByRaffle((prev) => {
      const next = new Map(prev);
      const current = next.get(raffle.id) ?? [];
      next.set(raffle.id, [newAttempt, ...current]);
      return next;
    });

    return attemptId;
  };

  const updateRafflePurchaseAttempt = async (
    raffleId: string,
    attemptId: string,
    updates: Record<string, unknown>,
  ) => {
    const { error } = await supabase
      .from("raffle_purchase_attempts")
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("attempt_id", attemptId);

    if (error) {
      throw error;
    }

    setPurchaseAttemptsByRaffle((prev) => {
      const next = new Map(prev);
      const attempts = next.get(raffleId) ?? [];
      next.set(
        raffleId,
        attempts.map((attempt) =>
          attempt.attempt_id === attemptId
            ? {
                ...attempt,
                ...updates,
                updated_at: new Date().toISOString(),
              }
            : attempt,
        ),
      );
      return next;
    });
  };

  const handleAddXseedToMetamask = async () => {
    if (!walletAddress) {
      toast.error("Connect your wallet first.");
      openLoginModal();
      return;
    }

    setAddTokenLoading(true);

    try {
      const { ethereumProvider, tokenContract } = await connectMetamaskForXseed();
      const [symbol, decimals] = await Promise.all([
        tokenContract
          .symbol()
          .catch(() => XSEED_TOKEN_SYMBOL),
        tokenContract
          .decimals()
          .catch(() => XSEED_TOKEN_DECIMALS),
      ]);

      const wasAdded = await ethereumProvider.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20",
          options: {
            address: XSEED_TOKEN_ADDRESS,
            symbol,
            decimals: Number(decimals),
            image: XSEED_TOKEN_IMAGE,
          },
        },
      });

      if (wasAdded) {
        toast.success("XSEED added to MetaMask.");
      }
    } catch (error: unknown) {
      console.error("Error adding XSEED to MetaMask:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to add XSEED to MetaMask.",
      );
    } finally {
      setAddTokenLoading(false);
    }
  };

  const handlePurchaseTickets = async (raffle: Raffle) => {
    if (!walletAddress || !user) {
      toast.error("Connect your wallet to purchase tickets.");
      openLoginModal();
      return;
    }

    const purchaseCount = Math.max(1, ticketCount);
    const totalCost = (raffle.ticket_price || 0) * purchaseCount;
    let attemptId = "";
    let purchaseSuccess = false;
    let txSubmitted = false;
    let txConfirmed = false;
    let purchaseHash = "";
    let purchaseError: unknown = null;

    if (totalCost <= 0) {
      toast.error("This raffle does not have a valid ticket price.");
      return;
    }

    try {
      setPurchaseLoading((prev) => ({ ...prev, [raffle.id]: true }));
      attemptId = await createRafflePurchaseAttempt({
        raffle,
        purchaseCount,
        totalCost,
      });

      const { tokenContract } = await connectMetamaskForXseed();
      const [decimals, symbol, balance] = await Promise.all([
        tokenContract.decimals(),
        tokenContract.symbol().catch(() => XSEED_TOKEN_SYMBOL),
        tokenContract.balanceOf(walletAddress),
      ]);

      const totalAmount = ethers.utils.parseUnits(
        totalCost.toString(),
        decimals,
      );

      if (balance.lt(totalAmount)) {
        throw new Error(`Insufficient ${symbol} balance for this purchase.`);
      }

      toast.info("MetaMask opened. Confirm the XSEED transfer to continue.");
      const transaction = await tokenContract.transfer(
        XSEED_RECEIVER_ADDRESS,
        totalAmount,
      );
      purchaseHash = transaction.hash;
      txSubmitted = true;

      await updateRafflePurchaseAttempt(raffle.id, attemptId, {
        status: "submitted",
        tx_hash: transaction.hash,
        submitted_at: new Date().toISOString(),
      });

      const receipt = await transaction.wait();
      if (receipt.status !== 1) {
        throw new Error("The XSEED transfer was not confirmed.");
      }
      txConfirmed = true;

      await updateRafflePurchaseAttempt(raffle.id, attemptId, {
        status: "confirmed",
        confirmed_at: new Date().toISOString(),
      });

      const updatedEntry = await upsertPurchasedTickets(raffle, purchaseCount);
      syncPurchasedTicketsLocally(
        raffle.id,
        purchaseCount,
        updatedEntry ?? undefined,
      );

      await updateRafflePurchaseAttempt(raffle.id, attemptId, {
        status: "db_recorded",
        db_recorded_at: new Date().toISOString(),
      });

      purchaseSuccess = true;
      setPurchaseModalRaffle(null);
      toast.success(
        `Purchased ${purchaseCount} ticket${purchaseCount > 1 ? "s" : ""} successfully.`,
      );
    } catch (error: unknown) {
      purchaseError = error;
      console.error("Error purchasing raffle tickets:", error);
      toast.error(
        purchaseHash
          ? `XSEED transfer succeeded, but the raffle entry update failed. Please contact support with tx ${purchaseHash}.`
          : error instanceof Error
            ? error.message
            : "Failed to purchase raffle tickets.",
      );
    } finally {
      setPurchaseLoading((prev) => ({ ...prev, [raffle.id]: false }));

      try {
        if (attemptId) {
          const errorMessage =
            purchaseError instanceof Error
              ? purchaseError.message
              : "Unknown error";
          const rejectionCode =
            purchaseError &&
            typeof purchaseError === "object" &&
            "code" in purchaseError
              ? Number((purchaseError as { code?: number | string }).code)
              : null;
          const rejectionMessage =
            purchaseError &&
            typeof purchaseError === "object" &&
            "message" in purchaseError
              ? String((purchaseError as { message?: string }).message || "")
              : "";
          const wasCancelled =
            rejectionCode === 4001 ||
            rejectionMessage.toLowerCase().includes("user rejected") ||
            rejectionMessage.toLowerCase().includes("user denied") ||
            rejectionMessage.toLowerCase().includes("rejected the transaction");

          if (!purchaseSuccess) {
            await updateRafflePurchaseAttempt(raffle.id, attemptId, {
              status: txConfirmed
                ? "needs_reconciliation"
                : wasCancelled
                  ? "cancelled"
                  : txSubmitted
                    ? "failed"
                    : "failed",
              tx_hash: purchaseHash || null,
              error_message: errorMessage,
            });
          }
        }

        const currentIp = await getIpAddress();
        const errorMessage =
          purchaseError instanceof Error
            ? purchaseError.message
            : "Unknown error";

        await logSysAudit({
          uid: user?.user_id || user?.uid,
          email: user?.email,
          ip_address: currentIp,
          success: purchaseSuccess,
          comment: purchaseSuccess
            ? `User purchased ${purchaseCount} raffle ticket(s) for raffle ${raffle.id}. Tx: ${purchaseHash}`
            : `Raffle ticket purchase failed for raffle ${raffle.id}: ${errorMessage}`,
          target: purchaseHash || walletAddress,
        });
      } catch (auditError) {
        console.error("Failed to log raffle purchase audit:", auditError);
      }
    }
  };

  // Claim handler function for raffle rewards
  const handleClaimRaffleReward = async (raffle: Raffle) => {
    let nonce, signature;
    let claimSuccess = false;
    let claimError: unknown = null;
    let rewardsResponse = null;

    console.log("▶️ handleClaimRaffleReward", {
      raffleId: raffle?.id,
      hasWinnerInfo: !!raffle?.winnerInfo,
      round_id: raffle?.winnerInfo?.round_id,
      walletAddress: walletAddress || "N/A",
      userId: user?.user_id || user?.uid || "N/A",
    });

    if (!raffle.winnerInfo || !walletAddress || !user) {
      toast.error("Unable to claim reward. Missing information.");
      console.warn("⚠️ Missing required claim data", {
        hasWinnerInfo: !!raffle.winnerInfo,
        hasWallet: !!walletAddress,
        hasUser: !!user,
      });
      return;
    }

    try {
      console.log("⏳ Setting claim loading state for raffle", raffle.id);
      setClaimLoading((prev) => ({ ...prev, [raffle.id]: true }));
      const prizeAmount = raffle.winnerInfo.prize_amount;
      const amountForSignature =
        process.env.NEXT_PUBLIC_VERCEL_ENV === "development" ? 10 : prizeAmount;

      // Generate token for API request
      console.log("🔐 Generating token", {
        raffle_id: raffle.id,
        round_id: raffle.winnerInfo.round_id,
        amount: amountForSignature,
      });
      const token = generateToken(walletAddress, {
        amount: amountForSignature,
        raffle_id: raffle.id,
        round_id: raffle.winnerInfo.round_id,
      });
      if (!token) throw new Error("Token validation failed.");
      console.log("✅ Token generated");

      // Fetch signature from API
      console.log("📡 Requesting signature from API...");
      const response = await fetch(
        // "http://127.0.0.1:5001/privatemxs/us-central1/generateRaffleRewardSignature",
        "https://us-central1-privatemxs.cloudfunctions.net/generateRaffleRewardSignature",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("📬 Signature API response", {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to generate signature");
      }

      const res = await response.json();
      ({ nonce, signature } = res);
      const { amount, expiry } = res;
      console.log("🧾 Signature payload", {
        hasNonce: !!nonce,
        hasSignature: !!signature,
        amount,
        expiry,
      });

      // Convert amount to BigNumber
      const amountInBigNumber = ethers.utils.parseEther(amount.toString());
      console.log("🔢 Parsed amount to BigNumber", {
        hex: amountInBigNumber._hex,
      });

      if (typeof window === "undefined") {
        throw new Error("This function must be run in the browser.");
      }

      // Get provider and signer
      console.log("🔌 Connecting provider & signer...");
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      console.log("🔑 Signer ready");

      // Initialize contract
      const rewardContract = new ethers.Contract(
        CONTRACT_ADDRESS_FOR_RAFFLE_REWARD_CLAIM,
        rewardAbi,
        signer,
      );
      console.log("📄 Contract initialized", {
        address: CONTRACT_ADDRESS_FOR_RAFFLE_REWARD_CLAIM,
      });

      // Execute claim transaction
      console.log("🧾 Sending claimReward tx...");
      rewardsResponse = await rewardContract.claimReward(
        amountInBigNumber._hex,
        nonce,
        expiry,
        signature,
      );
      console.log("🚀 Transaction submitted", {
        hash: rewardsResponse?.hash,
      });

      // Wait for transaction confirmation
      const receipt = await rewardsResponse.wait();
      console.log("📦 Tx receipt", {
        status: receipt?.status,
        transactionHash: receipt?.transactionHash,
      });

      if (receipt.status === 1) {
        toast.success("🎉 Claim successful!");
        claimSuccess = true;
        console.log("🎉 Claim succeeded, updating state");

        // Update the raffle's winnerInfo to mark as claimed
        if (activeTab === "active") {
          setActiveRaffles((prev) =>
            prev.map((r) =>
              r.id === raffle.id
                ? {
                    ...r,
                    winnerInfo: r.winnerInfo
                      ? {
                          ...r.winnerInfo,
                          claimed: true,
                          claimed_at: new Date().toISOString(),
                          transaction_hash: receipt.transactionHash,
                        }
                      : undefined,
                  }
                : r,
            ),
          );
        } else if (activeTab === "past") {
          setPastRaffles((prev) =>
            prev.map((r) =>
              r.id === raffle.id
                ? {
                    ...r,
                    winnerInfo: r.winnerInfo
                      ? {
                          ...r.winnerInfo,
                          claimed: true,
                          claimed_at: new Date().toISOString(),
                          transaction_hash: receipt.transactionHash,
                        }
                      : undefined,
                  }
                : r,
            ),
          );
        }

        // Refresh the page data after successful claim
        // You might want to refetch raffles here
        console.log("🔁 Post-claim actions queued");
      } else {
        toast.error("❌ Claim failed!");
        console.error("🛑 Tx failed with status !== 1");
        throw new Error("Transaction failed");
      }
    } catch (error: unknown) {
      claimError = error;
      console.error("💥 Error claiming raffle reward:", error);
      toast.error(
        (error as Error).message || "❌ Claim failed! Please try again.",
      );
    } finally {
      console.log("🧹 Finalizing claim flow for", raffle.id);
      setClaimLoading((prev) => ({ ...prev, [raffle.id]: false }));

      // Audit log
      try {
        console.log("📝 Writing sys_audit record...");
        const ip_address = await getIpAddress();

        const errorMessage =
          claimError &&
          typeof claimError === "object" &&
          "message" in claimError
            ? String(
                (claimError as { message?: string }).message || "Unknown error",
              )
            : "Unknown error";

        await logSysAudit({
          uid: user?.user_id || user?.uid,
          email: user?.email,
          ip_address,
          success: claimSuccess,
          comment: claimSuccess
            ? `User successfully claimed XSEED raffle reward for raffle ${raffle.id}.`
            : `Claim failed for raffle ${raffle.id}: ${errorMessage}`,
          target: walletAddress,
        });
        console.log("✅ sys_audit recorded");
      } catch (auditErr) {
        console.error("🧾 Audit log failed:", auditErr);
      }
    }
  };

  // History
  const history = [
    { id: "RAFFLE-000", date: "Dec 10", won: true, amount: 2.4 },
    { id: "RAFFLE-999", date: "Dec 5", won: false, amount: 0 },
    { id: "RAFFLE-998", date: "Dec 1", won: true, amount: 5.2 },
    { id: "RAFFLE-997", date: "Nov 28", won: false, amount: 0 },
  ];

  return (
    <div className="w-full min-h-screen bg-[#0a0a0b] p-6 mt-24">
      <div className="max-w-6xl mx-auto">
        <div className="flex gap-6">
          {/* Left Column: Tabs and Tab Content */}
          <div className="w-full md:w-4/6">
            {/* Tabs */}
            <div className="mb-6">
              <div className="inline-flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-[#121315] p-2 shadow-[0_16px_40px_rgba(0,0,0,0.2)]">
                <button
                  onClick={() => setActiveTab("active")}
                  className={`inline-flex min-w-[112px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] transition-all duration-200 ${
                    activeTab === "active"
                      ? "bg-[#1C2432] text-white shadow-[inset_0_0_0_1px_rgba(72,115,255,0.28)]"
                      : "bg-transparent text-[#8F98A8] hover:bg-white/[0.03] hover:text-white"
                  }`}
                >
                  <Zap
                    className={`h-4 w-4 ${
                      activeTab === "active"
                        ? "text-[#7EA2FF]"
                        : "text-[#6D7380]"
                    }`}
                  />
                  Active
                </button>
                <button
                  onClick={() => setActiveTab("past")}
                  className={`relative inline-flex min-w-[112px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] transition-all duration-200 ${
                    activeTab === "past"
                      ? "bg-[#1A1C20] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                      : "bg-transparent text-[#8F98A8] hover:bg-white/[0.03] hover:text-white"
                  }`}
                >
                  <History
                    className={`h-4 w-4 ${
                      activeTab === "past" ? "text-[#D5DAE3]" : "text-[#6D7380]"
                    }`}
                  />
                  Past
                  {unclaimedRewardsCount > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 grid h-5 min-w-[20px] place-items-center rounded-full border border-[#c88717] bg-[#F4A000] px-1 text-[10px] font-bold text-black shadow-sm">
                      {unclaimedRewardsCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab("upcoming")}
                  className={`inline-flex min-w-[126px] items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] transition-all duration-200 ${
                    activeTab === "upcoming"
                      ? "bg-[#171B27] text-white shadow-[inset_0_0_0_1px_rgba(101,123,255,0.24)]"
                      : "bg-transparent text-[#8F98A8] hover:bg-white/[0.03] hover:text-white"
                  }`}
                >
                  <Clock
                    className={`h-4 w-4 ${
                      activeTab === "upcoming"
                        ? "text-[#90A7FF]"
                        : "text-[#6D7380]"
                    }`}
                  />
                  Upcoming
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === "active" && (
              <div className="space-y-6">
                {loadingRaffles ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400">Loading raffles...</div>
                  </div>
                ) : errorRaffles ? (
                  <div className="text-center py-12">
                    <div className="text-red-400">{errorRaffles}</div>
                  </div>
                ) : activeRaffles.length === 0 ? (
                  renderEmptyState({
                    icon: <Zap className="h-6 w-6" />,
                    title: "No active raffles right now",
                    description:
                      "There are no live raffles at the moment. Check the upcoming tab to see what is scheduled next.",
                  })
                ) : (
                  activeRaffles.map((raffle) => (
                    <div
                      key={raffle.id}
                      className="bg-[#111113] border border-gray-800 rounded-xl p-6"
                    >
                      {/* Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Ticket className="h-5 w-5 text-yellow-500" />
                          <h2 className="text-lg font-semibold text-white">
                            {raffle.name}
                          </h2>
                        </div>
                        <span className="text-xs px-3 py-1 rounded-full bg-orange-600/20 border border-orange-500/30 text-orange-400 flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-orange-400 animate-pulse"></span>
                          Live
                        </span>
                      </div>

                      <div className="text-sm text-gray-500 flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {raffle.participants ?? 957} participants
                        </span>
                        <span className="text-gray-600">/</span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {(() => {
                            const endMs = new Date(raffle.end_date).getTime();
                            const diff = endMs - Date.now();
                            const days = Math.max(
                              0,
                              Math.floor(diff / (1000 * 60 * 60 * 24)),
                            );
                            const hrs = Math.max(
                              0,
                              Math.floor(
                                (diff % (1000 * 60 * 60 * 24)) /
                                  (1000 * 60 * 60),
                              ),
                            );
                            const mins = Math.max(
                              0,
                              Math.floor(
                                (diff % (1000 * 60 * 60)) / (1000 * 60),
                              ),
                            );
                            return `Ends in ${days}d ${String(hrs).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
                          })()}
                        </span>
                      </div>
                      <div className="h-px bg-gray-800 my-4" />

                      <div className="mb-1">
                        <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                          Prize Pool
                        </div>
                        <span className="text-3xl font-semibold text-white">
                          {raffle.prizePool.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400 ml-2">
                          XSEED
                        </span>
                      </div>
                      <div className="h-px bg-gray-800 my-4" />

                      {/* Ticket Purchase Section */}
                      <div className="mb-6 overflow-hidden rounded-[24px] border border-[#23272E] bg-[#121418] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                          <div>
                            <div className="mb-2 text-xs font-medium uppercase tracking-[0.16em] text-[#767C88]">
                              Price Per Ticket
                            </div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-[2rem] font-bold leading-none tracking-[-0.04em] text-white">
                                {raffle.ticket_price?.toLocaleString() ?? 0}
                              </span>
                              <span className="text-sm font-medium text-[#7E8694]">
                                XSEED
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 lg:justify-end">
                            <button
                              onClick={() =>
                                setTicketCount(Math.max(1, ticketCount - 1))
                              }
                              className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.04] bg-[#1A1D22] text-[1.7rem] leading-none text-[#949AA5] transition-colors hover:text-white"
                              aria-label="Decrease ticket quantity"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              value={ticketCount}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                setTicketCount(Math.max(1, val));
                              }}
                              className="h-12 w-[5.25rem] rounded-2xl border border-white/[0.04] bg-[#101217] text-center text-xl font-bold text-white outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <button
                              onClick={() => setTicketCount(ticketCount + 1)}
                              className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/[0.04] bg-[#1A1D22] text-[1.7rem] leading-none text-[#949AA5] transition-colors hover:text-white"
                              aria-label="Increase ticket quantity"
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <div className="my-5 h-px bg-[linear-gradient(90deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.10)_18%,rgba(255,255,255,0.10)_82%,rgba(255,255,255,0.02)_100%)]" />

                        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                          <div>
                            <div className="mb-2 text-sm font-medium text-[#7C828D]">
                              Total cost
                            </div>
                            <div className="flex items-baseline gap-2">
                              <span className="text-[2rem] font-bold leading-none tracking-[-0.04em] text-white">
                                {(
                                  (raffle.ticket_price || 0) * ticketCount
                                ).toLocaleString()}
                              </span>
                              <span className="text-sm font-medium text-[#7E8694]">
                                XSEED
                              </span>
                            </div>
                          </div>

                          <button
                            onClick={() => openPurchaseModal(raffle)}
                            className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(90deg,#FF7A00_0%,#FFB400_58%,#FFD54A_100%)] px-6 text-base font-semibold text-white shadow-[0_10px_30px_rgba(255,133,14,0.28)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(255,153,0,0.34)] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-[0_10px_30px_rgba(255,133,14,0.28)]"
                          >
                            <svg
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="1.8"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3.75 6.75h16.5a1.5 1.5 0 011.5 1.5v7.5a1.5 1.5 0 01-1.5 1.5H3.75a1.5 1.5 0 01-1.5-1.5v-7.5a1.5 1.5 0 011.5-1.5z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6.75 12h5.25M17.25 9.75h.008v.008h-.008zm0 4.5h.008v.008h-.008z"
                              />
                            </svg>
                            Purchase Tickets
                          </button>
                        </div>
                      </div>
                      {raffle.winners_selected && (
                        <div className="mb-6">
                          {raffle.winnerInfo ? (
                            raffle.winnerInfo?.claimed ? (
                              <div className="bg-[#202022] border border-[#383838] rounded-xl p-5 my-3">
                                <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                                  <span>
                                    <Check className="h-4 w-4" />
                                  </span>
                                  <span>Rewards Claimed!</span>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">
                                  Transaction Hash:{" "}
                                  <a
                                    href={`https://etherscan.io/tx/${
                                      raffle.winnerInfo.transaction_hash ?? ""
                                    }`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:underline break-all"
                                  >
                                    {raffle.winnerInfo.transaction_hash ?? ""}
                                  </a>
                                </p>
                                <p className="text-sm text-gray-500">
                                  Claimed on:{" "}
                                  {new Date(
                                    raffle.winnerInfo?.claimed_at ?? "",
                                  ).toLocaleString()}
                                </p>
                              </div>
                            ) : (
                              <div className="bg-[#2a1b0b] border border-orange-500/30 rounded-xl p-5 my-3">
                                <div className="flex items-center gap-2 text-orange-300 text-sm mb-2">
                                  <Sparkles className="h-4 w-4" />
                                  <span>You&apos;re eligible to claim</span>
                                </div>
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                  <div>
                                    <span className="text-2xl md:text-2xl font-semibold text-white">
                                      {Number(
                                        (
                                          raffle.winnerInfo as
                                            | RaffleWinner
                                            | undefined
                                        )?.prize_amount ?? 0,
                                      ).toLocaleString()}
                                    </span>
                                    <span className="text-sm text-orange-300 ml-2">
                                      XSEED
                                    </span>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleClaimRaffleReward(raffle)
                                    }
                                    disabled={
                                      claimLoading[raffle.id] ||
                                      claimStatusLoading
                                    }
                                    className="md:w-auto w-full bg-gradient-to-r from-orange-500 to-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed text-black font-semibold py-3 px-5 rounded-lg flex items-center justify-center gap-2 shadow-md"
                                  >
                                    {claimLoading[raffle.id] ? (
                                      <>
                                        <svg
                                          className="animate-spin w-5 h-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                          ></path>
                                        </svg>
                                        Claiming...
                                      </>
                                    ) : claimStatusLoading ? (
                                      <>
                                        <svg
                                          className="animate-spin w-5 h-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                          ></path>
                                        </svg>
                                        Checking status...
                                      </>
                                    ) : (
                                      <>
                                        <HandCoins className="w-4 h-4" />
                                        Claim Rewards
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="bg-[#202022] border border-[#383838] rounded-xl p-5 my-3">
                              <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                                <span>
                                  <Clock className="h-4 w-4" />
                                </span>
                                <span>
                                  Winners selected, but you did not win this
                                  time.
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">
                                Keep participating to increase your chances of
                                winning. More entries means better odds!
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="h-px bg-gray-800 my-6" />

                      {/* Your Total Entries */}
                      <div className="flex items-center justify-between py-3 border-b border-gray-800">
                        <span className="text-sm text-gray-400 flex items-center gap-2">
                          <Ticket className="h-4 w-4 text-yellow-500" />
                          Your Total Entries
                        </span>
                        <span className="text-lg font-semibold text-white">
                          {raffle.userEntries ?? 0}
                        </span>
                      </div>

                      {/* Entry Method Tabs */}
                      <div className="flex gap-2 mt-6 mb-4">
                        <button
                          onClick={() => setEntryMethod("gold")}
                          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${
                            entryMethod === "gold"
                              ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-black shadow rounded-lg"
                              : "bg-[#1f2022] text-gray-300 border border-gray-700 rounded-lg"
                          }`}
                        >
                          <Diamond className="h-4 w-4" />
                          Gold
                        </button>
                        <button
                          onClick={() => setEntryMethod("nft")}
                          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${
                            entryMethod === "nft"
                              ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-black shadow rounded-lg"
                              : "bg-[#1f2022] text-gray-300 border border-gray-700 rounded-lg"
                          }`}
                        >
                          <ImageIcon className="h-4 w-4" />
                          NFT
                        </button>
                        <button
                          onClick={() => setEntryMethod("purchased")}
                          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors ${
                            entryMethod === "purchased"
                              ? "bg-gradient-to-r from-orange-500 to-yellow-500 text-black shadow rounded-lg"
                              : "bg-[#1f2022] text-gray-300 border border-gray-700 rounded-lg"
                          }`}
                        >
                          <History className="h-4 w-4" />
                          Purchased
                        </button>
                      </div>

                      {/* Dynamic Entry Section */}
                      {entryMethod === "gold" &&
                        (() => {
                          const userEntry = raffleEntries.get(raffle.id);
                          const goldEntries = userEntry?.gold_entries ?? 0;
                          const goldCount = userEntry?.gold_count ?? 0;
                          return (
                            <div className="space-y-2 mb-6">
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Entry Ratio
                                </span>
                                <span className="text-sm text-white">
                                  Gold = 1 Entry
                                </span>
                              </div>
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Your Gold Count
                                </span>
                                <span className="text-sm font-semibold text-yellow-400">
                                  {goldCount.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Total Gold Entries
                                </span>
                                <span className="text-sm font-semibold text-white">
                                  {goldEntries.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          );
                        })()}

                      {entryMethod === "nft" &&
                        (() => {
                          const userEntry = raffleEntries.get(raffle.id);
                          const nftEntries = userEntry?.nft_entries ?? 0;
                          const nftCount = userEntry?.nft_count ?? 0;
                          const totalPower = userEntry?.total_power ?? 0;
                          return (
                            <div className="space-y-2 mb-6">
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Entry Ratio
                                </span>
                                <span className="text-sm text-white">
                                  x per NFT
                                </span>
                              </div>
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Total Power
                                </span>
                                <span className="text-sm font-semibold text-purple-400">
                                  {totalPower.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Your NFT Count
                                </span>
                                <span className="text-sm font-semibold text-purple-400">
                                  {nftCount.toLocaleString()}
                                </span>
                              </div>
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-400">
                                  Total NFT Entries
                                </span>
                                <span className="text-sm font-semibold text-white">
                                  {nftEntries.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          );
                        })()}

                      {entryMethod === "purchased" &&
                        (() => {
                          const userEntry = raffleEntries.get(raffle.id);
                          const purchasedEntries =
                            userEntry?.purchased_entries ?? 0;
                          const attempts =
                            purchaseAttemptsByRaffle.get(raffle.id) ?? [];
                          const currentPage = getPurchaseHistoryPage(raffle.id);
                          const totalPages = Math.max(
                            1,
                            Math.ceil(
                              attempts.length / PURCHASE_HISTORY_PAGE_SIZE,
                            ),
                          );
                          const safePage = Math.min(currentPage, totalPages);
                          const successfulAttempts = attempts.filter((attempt) =>
                            purchaseSuccessStatuses.includes(attempt.status),
                          );
                          const totalSpent = successfulAttempts.reduce(
                            (sum, attempt) => sum + Number(attempt.total_price ?? 0),
                            0,
                          );
                          const historyPreview = attempts.slice(
                            (safePage - 1) * PURCHASE_HISTORY_PAGE_SIZE,
                            safePage * PURCHASE_HISTORY_PAGE_SIZE,
                          );

                          return (
                            <div className="mb-6 space-y-4">
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div className="rounded-xl border border-white/5 bg-[#15171C] p-4">
                                  <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                                    Total Purchased Entries
                                  </div>
                                  <div className="mt-2 text-2xl font-semibold text-white">
                                    {purchasedEntries.toLocaleString()}
                                  </div>
                                </div>
                                <div className="rounded-xl border border-[#3C2A12] bg-[linear-gradient(180deg,#23190D_0%,#17120C_100%)] p-4">
                                  <div className="text-xs uppercase tracking-[0.16em] text-[#D0A267]">
                                    Total Purchase Value
                                  </div>
                                  <div className="mt-2 text-2xl font-semibold text-white">
                                    {totalSpent.toLocaleString()}
                                  </div>
                                  <div className="mt-1 text-xs text-[#F4C27E]">
                                    XSEED
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-xl border border-white/5 bg-[#111318]">
                                <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
                                  <div>
                                    <div className="text-sm font-semibold text-white">
                                      Purchase History
                                    </div>
                                    <div className="text-xs text-[#8891A1]">
                                      Recent raffle ticket purchase attempts
                                    </div>
                                  </div>
                                  <div className="text-xs text-[#8891A1]">
                                    {attempts.length} total
                                  </div>
                                </div>

                                {historyPreview.length === 0 ? (
                                  <div className="px-4 py-5 text-sm text-gray-400">
                                    No purchase history yet for this raffle.
                                  </div>
                                ) : (
                                  <>
                                    <div className="divide-y divide-white/5">
                                      {historyPreview.map((attempt) => (
                                        <div
                                          key={attempt.attempt_id}
                                          className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm font-semibold text-white">
                                                {attempt.ticket_count.toLocaleString()} tickets
                                              </span>
                                              <span
                                                className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${getPurchaseStatusTone(
                                                  attempt.status,
                                                )}`}
                                              >
                                                {attempt.status.replace("_", " ")}
                                              </span>
                                            </div>
                                            <div className="mt-1 text-xs text-[#8891A1]">
                                              {formatAttemptDate(
                                                attempt.db_recorded_at ||
                                                  attempt.confirmed_at ||
                                                  attempt.submitted_at ||
                                                  attempt.created_at,
                                              )}
                                            </div>
                                            <div className="mt-1 text-xs text-[#6F7785]">
                                              {attempt.tx_hash ? (
                                                <a
                                                  href={`${SNOWTRACE_TX_BASE_URL}${attempt.tx_hash}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center gap-1 text-[#8AB4FF] transition-colors hover:text-white"
                                                >
                                                  <span>
                                                    {`${attempt.tx_hash.slice(0, 8)}...${attempt.tx_hash.slice(-6)}`}
                                                  </span>
                                                  <ExternalLink className="h-3.5 w-3.5" />
                                                </a>
                                              ) : (
                                                "No transaction hash"
                                              )}
                                            </div>
                                          </div>

                                          <div className="flex items-center gap-3 sm:text-right">
                                            <div>
                                              <div className="text-sm font-semibold text-white">
                                                {Number(
                                                  attempt.total_price ?? 0,
                                                ).toLocaleString()}{" "}
                                                XSEED
                                              </div>
                                              <div className="text-xs text-[#8891A1]">
                                                {Number(
                                                  attempt.ticket_price ?? 0,
                                                ).toLocaleString()}{" "}
                                                each
                                              </div>
                                            </div>
                                            <button
                                              onClick={() =>
                                                setSelectedPurchaseAttempt(attempt)
                                              }
                                              className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/[0.06]"
                                            >
                                              View details
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>

                                    {attempts.length > PURCHASE_HISTORY_PAGE_SIZE && (
                                      <div className="flex items-center justify-between border-t border-white/5 px-4 py-3">
                                        <button
                                          onClick={() =>
                                            setPurchaseHistoryPage(
                                              raffle.id,
                                              Math.max(1, safePage - 1),
                                            )
                                          }
                                          disabled={safePage === 1}
                                          className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                          Previous
                                        </button>
                                        <div className="text-xs text-[#8891A1]">
                                          Page {safePage} of {totalPages}
                                        </div>
                                        <button
                                          onClick={() =>
                                            setPurchaseHistoryPage(
                                              raffle.id,
                                              Math.min(totalPages, safePage + 1),
                                            )
                                          }
                                          disabled={safePage === totalPages}
                                          className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                          Next
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === "past" && (
              <div className="space-y-4">
                {unclaimedRewardsCount > 0 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 px-4 py-3 rounded-lg mb-6 flex items-center gap-3 shadow-sm">
                    <Trophy className="h-5 w-5 text-yellow-400" />
                    <div>
                      <span className="font-semibold text-base mr-1">
                        You have {unclaimedRewardsCount} unclaimed reward
                        {unclaimedRewardsCount !== 1 ? "s" : ""}.
                      </span>
                      <span className="block text-xs text-yellow-300/80">
                        Ready when you are.
                      </span>
                    </div>
                  </div>
                )}
                {pastRaffles.length === 0
                  ? renderEmptyState({
                      icon: <History className="h-6 w-6" />,
                      title: "No past raffles yet",
                      description:
                        "Completed raffles will appear here once results have been finalized and published.",
                    })
                  : pastRaffles.map((raffle) => {
                      const dateParts = formatPastRaffleDate(raffle.end_date);
                      const status = getPastRaffleStatus(raffle);
                      const isClaimed = status.tone === "claimed";
                      const isUnclaimed = status.tone === "unclaimed";
                      const isLost = status.tone === "lost";

                      return (
                        <div
                          key={raffle.id}
                          className="overflow-hidden rounded-[20px] border border-white/10 bg-[#121212] shadow-[0_20px_60px_rgba(0,0,0,0.28)]"
                        >
                          <div className="p-4 sm:p-5 lg:p-6">
                            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between xl:gap-7">
                              <div className="flex-1">
                                <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs">
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[#A6A6A6]">
                                    <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                    Ended
                                  </span>
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[#A6A6A6]">
                                    <Users className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                    {(
                                      raffle.participants ?? 0
                                    ).toLocaleString()}{" "}
                                    entries
                                  </span>
                                </div>

                                <h3 className="text-lg font-semibold leading-tight tracking-[-0.03em] text-white sm:text-2xl xl:text-3xl">
                                  {raffle.name}
                                </h3>
                                <p className="mt-2.5 max-w-xl text-xs leading-6 text-[#A6A6A6] sm:text-sm xl:max-w-2xl">
                                  {getPastRaffleSubtitle(raffle)}
                                </p>

                                <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs sm:text-sm">
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-[#E8E8E8]">
                                    <Trophy className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#F4A000]" />
                                    <span>
                                      {raffle.winnerInfo
                                        ? "Winner selected"
                                        : "No win"}
                                    </span>
                                  </div>
                                  <span className="text-white/15">|</span>
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-[#E8E8E8]">
                                    <Coins className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#F4A000]" />
                                    <span className="font-semibold text-white">
                                      {raffle.prizePool.toLocaleString()} XSEED
                                    </span>
                                    <span className="text-[#A6A6A6]">
                                      Prize Pool
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="w-full rounded-xl border border-white/[0.06] bg-white/[0.04] p-4 sm:p-4.5 xl:max-w-[244px]">
                                <div className="mb-3.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#BDBDBD] sm:text-xs">
                                  <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                  <span>Ended</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                                  {[
                                    { label: dateParts.month, meta: "Month" },
                                    { label: dateParts.day, meta: "Day" },
                                    { label: dateParts.year, meta: "Year" },
                                  ].map((item) => (
                                    <div
                                      key={`${raffle.id}-${item.meta}`}
                                      className="rounded-xl bg-white/[0.05] px-2.5 py-3 text-center sm:px-3 sm:py-3.5"
                                    >
                                      <div className="text-2xl font-semibold text-white sm:text-lg xl:text-xl">
                                        {item.label}
                                      </div>
                                      <div className="mt-2 text-[9px] uppercase tracking-[0.2em] text-[#8C8C8C] sm:text-[10px]">
                                        {item.meta}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-white/[0.06] px-4 py-4 sm:px-5 sm:py-5 lg:px-6">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
                              <div className="flex items-start gap-3 sm:gap-4">
                                <div
                                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full sm:h-11 sm:w-11 ${
                                    isLost ? "bg-white/[0.08]" : "bg-[#3A2A08]"
                                  }`}
                                >
                                  {isLost ? (
                                    <XCircle className="h-4 w-4 text-[#8E8E8E] sm:h-5 sm:w-5" />
                                  ) : (
                                    <Sparkles className="h-4 w-4 text-[#F4A000] sm:h-5 sm:w-5" />
                                  )}
                                </div>

                                <div>
                                  <div className="text-base font-semibold leading-snug text-white sm:text-lg">
                                    {status.title}
                                  </div>
                                  <div
                                    className={`mt-1 text-xs leading-5 sm:text-sm ${
                                      isLost
                                        ? "text-[#A6A6A6]"
                                        : "text-[#D5D5D5]"
                                    }`}
                                  >
                                    {isLost ? (
                                      "Better luck next time! Keep participating for your chance to win."
                                    ) : (
                                      <>
                                        <span className="text-[#A6A6A6]">
                                          Your Prize:{" "}
                                        </span>
                                        <span className="font-semibold text-[#F4A000]">
                                          {Number(
                                            raffle.winnerInfo?.prize_amount ??
                                              0,
                                          ).toLocaleString()}{" "}
                                          XSEED
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-2.5 md:justify-end">
                                {isClaimed && (
                                  <div className="inline-flex items-center gap-2 rounded-xl bg-[#0B4F35] px-3.5 py-2.5 text-xs font-medium text-[#6BF0B0] sm:text-sm">
                                    <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                    Prize Claimed
                                  </div>
                                )}

                                {isUnclaimed && (
                                  <button
                                    onClick={() =>
                                      handleClaimRaffleReward(raffle)
                                    }
                                    disabled={
                                      claimLoading[raffle.id] ||
                                      claimStatusLoading
                                    }
                                    className="inline-flex min-w-[132px] items-center justify-center gap-2 rounded-xl bg-[#F4A000] px-3.5 py-2.5 text-xs font-semibold text-black hover:bg-[#dd9200] sm:min-w-[148px] sm:px-4 sm:py-3 sm:text-sm disabled:cursor-not-allowed disabled:bg-[#8c630b] disabled:text-black/70"
                                  >
                                    {claimLoading[raffle.id] ? (
                                      <>
                                        <RefreshCw className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" />
                                        Claiming...
                                      </>
                                    ) : claimStatusLoading ? (
                                      <>
                                        <RefreshCw className="h-3.5 w-3.5 animate-spin sm:h-4 sm:w-4" />
                                        Checking...
                                      </>
                                    ) : (
                                      <>
                                        <HandCoins className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                        Claim Prize
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                {false &&
                  (() => {
                    const unclaimed = pastRaffles.filter(
                      (r) => r.winnerInfo && !r.winnerInfo.claimed,
                    );
                    const others = pastRaffles.filter(
                      (r) => !(r.winnerInfo && !r.winnerInfo.claimed),
                    );
                    return (
                      <>
                        {unclaimed.map((raffle) => (
                          <div
                            key={`unclaimed-${raffle.id}`}
                            className="bg-[#111113] border border-gray-800 rounded-xl p-6"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <Ticket className="h-5 w-5 text-gray-400" />
                                <h2 className="text-lg font-semibold text-white">
                                  {raffle.name}
                                </h2>
                              </div>
                            </div>
                            <div className="mb-1">
                              <span className="text-4xl font-bold text-yellow-400">
                                {raffle.prizePool.toLocaleString()}
                              </span>
                              <span className="text-lg text-gray-400 ml-2">
                                XSEED
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mb-6">
                              {raffle.participants ?? 0} participants
                            </div>
                            {raffle.winners_selected &&
                              raffle.winnerInfo &&
                              !raffle.winnerInfo.claimed && (
                                <div className="bg-[#1d1e14] border border-[#fd8802]/40 rounded-xl p-5 my-3">
                                  <div className="flex items-center gap-2 text-green-400 text-sm mb-3">
                                    <span>🎉</span>
                                    <span>
                                      Congratulations! You&apos;re eligible to
                                      claim.
                                    </span>
                                  </div>
                                  <div className="mb-3">
                                    <span className="text-3xl font-bold text-white">
                                      {Number(
                                        (
                                          raffle.winnerInfo as
                                            | RaffleWinner
                                            | undefined
                                        )?.prize_amount ?? 0,
                                      ).toLocaleString()}
                                    </span>
                                    <span className="text-sm text-gray-400 ml-2">
                                      XSEED
                                    </span>
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleClaimRaffleReward(raffle)
                                    }
                                    disabled={
                                      claimLoading[raffle.id] ||
                                      claimStatusLoading
                                    }
                                    className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-700 disabled:cursor-not-allowed text-white font-medium py-3 rounded-xl flex items-center justify-center gap-2"
                                  >
                                    {claimLoading[raffle.id] ? (
                                      <>
                                        <svg
                                          className="animate-spin w-5 h-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                          ></path>
                                        </svg>
                                        Claiming...
                                      </>
                                    ) : claimStatusLoading ? (
                                      <>
                                        <svg
                                          className="animate-spin w-5 h-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                          ></path>
                                        </svg>
                                        Checking status...
                                      </>
                                    ) : (
                                      <>
                                        <HandCoins className="w-4 h-4" />
                                        Claim Rewards
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            <div className="flex items-center justify-between py-3 border-b border-gray-800">
                              <span className="text-sm text-gray-400">
                                Your Total Entries
                              </span>
                              <span className="text-lg font-semibold text-white">
                                {raffle.userEntries ?? 0}
                              </span>
                            </div>
                            <div className="flex gap-2 mt-6 mb-4">
                              <button
                                onClick={() => setEntryMethod("gold")}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  entryMethod === "gold"
                                    ? "bg-yellow-600/20 text-yellow-400 border border-yellow-600/50"
                                    : "bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-gray-600"
                                }`}
                              >
                                <Coins className="h-4 w-4" />
                                Gold
                              </button>
                              <button
                                onClick={() => setEntryMethod("nft")}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  entryMethod === "nft"
                                    ? "bg-purple-600/20 text-purple-400 border border-purple-600/50"
                                    : "bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-gray-600"
                                }`}
                              >
                                <ImageIcon className="h-4 w-4" />
                                NFT
                              </button>
                            </div>
                            {entryMethod === "gold" &&
                              (() => {
                                const userEntry = raffleEntries.get(raffle.id);
                                const goldEntries =
                                  userEntry?.gold_entries ?? 0;
                                const goldCount = userEntry?.gold_count ?? 0;
                                return (
                                  <div className="space-y-2 mb-6">
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Entry Ratio
                                      </span>
                                      <span className="text-sm text-white">
                                        Gold = 1 Entry
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Your Gold Count
                                      </span>
                                      <span className="text-sm font-semibold text-yellow-400">
                                        {goldCount.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Total Gold Entries
                                      </span>
                                      <span className="text-sm font-semibold text-white">
                                        {goldEntries.toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })()}
                            {entryMethod === "nft" &&
                              (() => {
                                const userEntry = raffleEntries.get(raffle.id);
                                const nftEntries = userEntry?.nft_entries ?? 0;
                                const nftCount = userEntry?.nft_count ?? 0;
                                const totalPower = userEntry?.total_power ?? 0;
                                return (
                                  <div className="space-y-2 mb-6">
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Entry Ratio
                                      </span>
                                      <span className="text-sm text-white">
                                        x per NFT
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Total Power
                                      </span>
                                      <span className="text-sm font-semibold text-purple-400">
                                        {totalPower.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Your NFT Count
                                      </span>
                                      <span className="text-sm font-semibold text-purple-400">
                                        {nftCount.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2">
                                      <span className="text-sm text-gray-400">
                                        Total NFT Entries
                                      </span>
                                      <span className="text-sm font-semibold text-white">
                                        {nftEntries.toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })()}
                          </div>
                        ))}
                        {others.map((raffle) => (
                          <div
                            key={raffle.id}
                            className="bg-[#171717] border border-[#222] rounded-2xl px-6 py-6 my-4 shadow-lg"
                          >
                            {/* Winner Status Section for Past Raffles */}
                            {raffle.winners_selected && (
                              <div className="mb-6">
                                {raffle.winnerInfo ? (
                                  raffle.winnerInfo?.claimed ? (
                                    <div className="bg-[#202022] border border-[#383838] rounded-xl p-5 my-3">
                                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                                        <span>
                                          <Check className="h-4 w-4" />
                                        </span>
                                        <span>Rewards Claimed!</span>
                                      </div>
                                      <p className="text-sm text-gray-500 mb-2">
                                        Transaction Hash:{" "}
                                        <a
                                          href={`https://etherscan.io/tx/${
                                            raffle.winnerInfo
                                              .transaction_hash ?? ""
                                          }`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-400 hover:underline break-all"
                                        >
                                          {raffle.winnerInfo.transaction_hash ??
                                            ""}
                                        </a>
                                      </p>
                                      <p className="text-sm text-gray-500">
                                        Claimed on:{" "}
                                        {new Date(
                                          raffle.winnerInfo?.claimed_at ?? "",
                                        ).toLocaleString()}
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="bg-[#1d1e14] border border-[#fd8802]/40 rounded-xl p-5 my-3">
                                      <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2 text-orange-300 text-sm">
                                          <span>🏆</span>
                                          <span>You won this raffle!</span>
                                        </div>
                                        <div className="text-right">
                                          <div className="text-3xl font-bold text-white">
                                            {Number(
                                              (
                                                raffle.winnerInfo as
                                                  | RaffleWinner
                                                  | undefined
                                              )?.prize_amount ?? 0,
                                            ).toLocaleString()}
                                          </div>
                                          <div className="text-xs text-gray-400">
                                            XSEED
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() =>
                                          handleClaimRaffleReward(raffle)
                                        }
                                        disabled={
                                          claimLoading[raffle.id] ||
                                          claimStatusLoading
                                        }
                                        className="w-full bg-[#fd8802] hover:bg-[#e97300] disabled:bg-orange-900 text-base font-semibold text-black tracking-wide py-3 rounded-full shadow-md flex items-center justify-center gap-2 transition-all duration-150"
                                      >
                                        {claimLoading[raffle.id] ? (
                                          <>
                                            <svg
                                              className="animate-spin w-5 h-5"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                            >
                                              <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                              ></circle>
                                              <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8z"
                                              ></path>
                                            </svg>
                                            Claiming...
                                          </>
                                        ) : claimStatusLoading ? (
                                          <>
                                            <svg
                                              className="animate-spin w-5 h-5"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                            >
                                              <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                              ></circle>
                                              <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8z"
                                              ></path>
                                            </svg>
                                            Checking status...
                                          </>
                                        ) : (
                                          <>
                                            <svg
                                              className="w-5 h-5"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth={2}
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                d="M12 8v4l3 3"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                              />
                                            </svg>
                                            Claim Rewards
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  )
                                ) : (
                                  <div className="bg-[#202022] border border-[#383838] rounded-xl p-5 my-3">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                                      <span>
                                        <Clock className="h-4 w-4" />
                                      </span>
                                      <span>
                                        Winners selected, but you did not win
                                        this time.
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                      Keep participating to increase your
                                      chances of winning. More entries means
                                      better odds!
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    raffle.won ? "bg-green-600" : "bg-gray-700"
                                  }`}
                                >
                                  {raffle.won ? (
                                    <Trophy className="h-5 w-5 text-white" />
                                  ) : (
                                    <X className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h3 className="text-base font-semibold text-white">
                                      RAFFLE-{raffle.name}
                                    </h3>
                                    <span className="text-sm text-gray-500">
                                      -
                                    </span>
                                    {raffle.winnerInfo &&
                                      !raffle.winnerInfo.claimed && (
                                        <Badge className="bg-[#fd8802] text-black font-bold text-xs uppercase rounded-full px-2.5 py-0.5 tracking-wide shadow-sm mr-2 border border-[#c86a00]">
                                          Unclaimed
                                        </Badge>
                                      )}
                                    {raffle.winnerInfo &&
                                      raffle.winnerInfo.claimed && (
                                        <Badge className="bg-[#11703a] text-white font-bold text-xs uppercase rounded-full px-2.5 py-0.5 tracking-wide shadow-sm mr-2 border border-[#0a4726]">
                                          Claimed
                                        </Badge>
                                      )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {raffle.participants ?? 0} participants
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-lg font-bold text-yellow-400">
                                  {raffle.prizePool.toLocaleString()}{" "}
                                  <span className="text-sm text-gray-400">
                                    XSEED
                                  </span>
                                </div>
                                {raffle.winnerInfo ? (
                                  <span className="text-sm text-green-400">
                                    Won +
                                    {Number(
                                      (
                                        raffle.winnerInfo as
                                          | RaffleWinner
                                          | undefined
                                      )?.prize_amount ?? 0,
                                    ).toLocaleString()}{" "}
                                    XSEED
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-500">
                                    No Win
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()}
              </div>
            )}

            {activeTab === "upcoming" && (
              <div className="space-y-4">
                {upcomingRaffles.length === 0
                  ? renderEmptyState({
                      icon: <Clock className="h-6 w-6" />,
                      title: "No upcoming raffles scheduled",
                      description:
                        "There are no upcoming raffles available right now. Please check back later for the next draw.",
                    })
                  : upcomingRaffles.map((raffle) => {
                      const countdown = getUpcomingRaffleCountdown(raffle);

                      return (
                        <div
                          key={raffle.id}
                          className="overflow-hidden rounded-[20px] border border-[#1A2254] bg-[linear-gradient(90deg,#10132B_0%,#0E1020_68%,#121317_100%)] shadow-[0_20px_60px_rgba(0,0,0,0.28)]"
                        >
                          <div className="p-4 sm:p-5 lg:p-6">
                            <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between xl:gap-7">
                              <div className="flex-1">
                                <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs">
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#2D62FF]/50 bg-[#163AAB]/20 px-2.5 py-1 text-[#8AB2FF]">
                                    <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                    Upcoming
                                  </span>
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[#A6A6A6]">
                                    <Users className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                    {(
                                      raffle.participants ?? 0
                                    ).toLocaleString()}{" "}
                                    entries
                                  </span>
                                </div>

                                <h3 className="text-lg font-semibold leading-tight tracking-[-0.03em] text-white sm:text-2xl xl:text-3xl">
                                  {raffle.name}
                                </h3>
                                <p className="mt-2.5 max-w-xl text-xs leading-6 text-[#A6A6A6] sm:text-sm xl:max-w-2xl">
                                  {getUpcomingRaffleSubtitle(raffle)}
                                </p>

                                <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs sm:text-sm">
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-[#E8E8E8]">
                                    <Trophy className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#F4A000]" />
                                    <span>Upcoming Draw</span>
                                  </div>
                                  <span className="text-white/15">|</span>
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-[#E8E8E8]">
                                    <Coins className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#F4A000]" />
                                    <span className="font-semibold text-white">
                                      {raffle.prizePool.toLocaleString()} XSEED
                                    </span>
                                    <span className="text-[#A6A6A6]">
                                      Prize Pool
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="w-full rounded-xl border border-white/[0.08] bg-[#0F1014] p-4 xl:max-w-[272px]">
                                <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#8F8F92] sm:text-xs">
                                  <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                                  <span>Starts In</span>
                                </div>

                                <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2 sm:gap-2.5">
                                  {[
                                    { value: countdown.days, label: "Days" },
                                    { value: countdown.hours, label: "Hours" },
                                    { value: countdown.minutes, label: "Mins" },
                                  ].map((item, index) => (
                                    <Fragment
                                      key={`${raffle.id}-${item.label}`}
                                    >
                                      <div className="rounded-xl bg-white/[0.94] px-2 py-3 text-center sm:px-3 sm:py-3.5">
                                        <div className="text-[1.45rem] font-semibold leading-none text-[#171717] sm:text-[1.7rem]">
                                          {item.value}
                                        </div>
                                        <div className="mt-2 text-[9px] uppercase tracking-[0.18em] text-[#5E5E62] sm:text-[10px]">
                                          {item.label}
                                        </div>
                                      </div>
                                      {index < 2 && (
                                        <div className="text-2xl font-semibold leading-none text-white/75 sm:text-[1.75rem]">
                                          :
                                        </div>
                                      )}
                                    </Fragment>
                                  ))}
                                </div>

                                <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-white/10">
                                  <div
                                    className="h-full rounded-full bg-[linear-gradient(90deg,#2D62FF_0%,#5B4CFF_100%)]"
                                    style={{ width: `${countdown.progress}%` }}
                                  />
                                </div>
                                <p className="mt-3 text-sm text-[#A6A6A6]">
                                  {countdown.progress}% of time until start
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
              </div>
            )}
          </div>

          {/* Right Column: Stats and History (always visible) */}
          <div className="w-full md:w-2/6 space-y-6">
            {/* My Raffle Stats */}
            <div className="bg-[#171717] border border-[#222] rounded-2xl px-6 py-6 my-4 shadow-lg">
              <div className="flex items-center gap-2 mb-5">
                <Ticket className="h-5 w-5 text-white" />
                <h3 className="text-base font-semibold text-white">
                  My Raffle Stats
                </h3>
              </div>

              <div className="divide-y divide-gray-800">
                <div className="py-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-400">
                    <BarChart3 className="h-4 w-4 text-white" />
                    Total Entries
                  </span>
                  <span className="text-lg font-semibold text-white">
                    {stats.totalEntries}
                  </span>
                </div>
                <div className="py-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-400">
                    <Trophy className="h-4 w-4 text-white" />
                    Wins
                  </span>
                  <span className="text-lg font-semibold text-white">
                    {stats.wins}
                  </span>
                </div>
                <div className="py-3 flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm text-gray-400">
                    <Percent className="h-4 w-4 text-white" />
                    Win Rate
                  </span>
                  <span className="text-lg font-semibold text-white">
                    {stats.winRate}%
                  </span>
                </div>
              </div>
              <div className="mt-5 bg-[#2a1b0b] border border-orange-500/30 rounded-xl px-4 py-4">
                <div className="flex items-center gap-2 text-orange-300 text-sm mb-2">
                  <Coins className="h-4 w-4" />
                  <span>Total Winnings</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-white">
                    {Number(stats.totalWinnings || 0).toLocaleString()}
                  </span>
                  <span className="text-xs text-orange-300">XSEED</span>
                </div>
              </div>
            </div>

            {/* Recent History */}
            <div className="bg-[#171717] border border-[#222] rounded-2xl px-6 py-6 my-4 shadow-lg">
              <div className="flex items-center gap-2 mb-5">
                <RefreshCw className="h-4 w-4 text-white" />
                <h3 className="text-base font-semibold text-white">
                  Recent History
                </h3>
              </div>

              <div className="divide-y divide-gray-800">
                {dynamicHistory.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 py-3">
                    <div className="w-6 h-6 flex items-center justify-center">
                      {item.won ? (
                        <CheckCircle className="h-5 w-5 text-blue-400" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white truncate">
                        {item.name}
                      </div>
                      <div className="text-xs text-gray-500">{item.date}</div>
                    </div>
                    <div className="text-right">
                      {item.won ? (
                        <span className="text-sm font-medium text-blue-400">
                          +
                          {Number(item.amount).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                            minimumFractionDigits: 0,
                          })}{" "}
                          XSEED
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">No Win</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        activeModal={!!purchaseModalRaffle}
        onClose={closePurchaseModal}
        noFade={false}
        disableBackdrop={false}
        centered
        dismissable={!(
          purchaseModalRaffle && purchaseLoading[purchaseModalRaffle.id]
        )}
        title={
          purchaseModalRaffle
            ? `Purchase Tickets: ${purchaseModalRaffle.name}`
            : "Purchase Tickets"
        }
        className="max-w-2xl bg-[#0F1116]"
        themeClass="border-b border-white/10 bg-[#12151C]"
        bodyClass="bg-[#0F1116]"
        footerContent={null}
        scrollContent={false}
        uncontrol={false}
        label=""
        titleClass=""
        labelClass=""
        footerClass=""
        fireworks={false}
        ref={null}
      >
        {purchaseModalRaffle && (
          <div className="space-y-5 text-white">
            <div className="rounded-2xl border border-[#242935] bg-[#131821] p-5">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1F2734] text-sm font-bold text-[#FFB54A]">
                  1
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">
                    Prepare XSEED In MetaMask
                  </h3>
                  <p className="mt-1 text-sm text-[#98A2B3]">
                    If XSEED is not visible in MetaMask yet, add it before continuing.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-white/5 bg-[#0D1117] p-4 text-sm text-[#B6BECA]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-white">Token</div>
                    <div>{XSEED_TOKEN_SYMBOL}</div>
                  </div>
                  <button
                    onClick={handleAddXseedToMetamask}
                    disabled={addTokenLoading}
                    className="inline-flex h-10 items-center justify-center rounded-xl border border-[#2B3240] bg-[#181D25] px-4 text-sm font-semibold text-white transition-colors hover:border-[#4A5568] hover:bg-[#1D2430] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {addTokenLoading ? "Adding XSEED..." : "Add XSEED"}
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#242935] bg-[#131821] p-5">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1F2734] text-sm font-bold text-[#FFB54A]">
                  2
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">
                    Review Summary
                  </h3>
                  <p className="mt-1 text-sm text-[#98A2B3]">
                    Confirm your ticket quantity and total XSEED cost before launching MetaMask.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-white/5 bg-[#0D1117] p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-[#6F7785]">
                    Tickets
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {purchaseTicketCount}
                  </div>
                </div>
                <div className="rounded-xl border border-white/5 bg-[#0D1117] p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-[#6F7785]">
                    Price Per Ticket
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {(purchaseModalRaffle.ticket_price || 0).toLocaleString()}
                  </div>
                  <div className="mt-1 text-xs text-[#98A2B3]">XSEED</div>
                </div>
                <div className="rounded-xl border border-[#3C2A12] bg-[linear-gradient(180deg,#23190D_0%,#17120C_100%)] p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-[#D0A267]">
                    Total Cost
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-white">
                    {purchaseTotalCost.toLocaleString()}
                  </div>
                  <div className="mt-1 text-xs text-[#F4C27E]">XSEED</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-[#242935] bg-[#131821] p-5">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1F2734] text-sm font-bold text-[#FFB54A]">
                  3
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">
                    Launch MetaMask
                  </h3>
                  <p className="mt-1 text-sm text-[#98A2B3]">
                    This sends {purchaseTotalCost.toLocaleString()} XSEED to the raffle receiver wallet and records your purchased entries after confirmation.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-[#7F8793]">
                  Receiver: {`${XSEED_RECEIVER_ADDRESS.slice(0, 6)}...${XSEED_RECEIVER_ADDRESS.slice(-4)}`}
                </div>
                <button
                  onClick={() => handlePurchaseTickets(purchaseModalRaffle)}
                  disabled={purchaseLoading[purchaseModalRaffle.id]}
                  className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(90deg,#FF7A00_0%,#FFB400_58%,#FFD54A_100%)] px-6 text-base font-semibold text-white shadow-[0_10px_30px_rgba(255,133,14,0.28)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(255,153,0,0.34)] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-[0_10px_30px_rgba(255,133,14,0.28)]"
                >
                  {purchaseLoading[purchaseModalRaffle.id] ? (
                    <>
                      <svg
                        className="h-5 w-5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Purchase Tickets"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        activeModal={!!selectedPurchaseAttempt}
        onClose={() => setSelectedPurchaseAttempt(null)}
        noFade={false}
        disableBackdrop={false}
        centered
        dismissable
        title="Purchase Details"
        className="max-w-xl bg-[#0F1116]"
        themeClass="border-b border-white/10 bg-[#12151C]"
        bodyClass="bg-[#0F1116]"
        footerContent={null}
        scrollContent={false}
        uncontrol={false}
        label=""
        titleClass=""
        labelClass=""
        footerClass=""
        fireworks={false}
        ref={null}
      >
        {selectedPurchaseAttempt && (
          <div className="space-y-4 text-sm text-[#C7CDD8]">
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${getPurchaseStatusTone(
                  selectedPurchaseAttempt.status,
                )}`}
              >
                {selectedPurchaseAttempt.status.replace("_", " ")}
              </span>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-white/5 bg-[#15171C] p-4">
                <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                  Tickets
                </div>
                <div className="mt-2 text-xl font-semibold text-white">
                  {selectedPurchaseAttempt.ticket_count.toLocaleString()}
                </div>
              </div>
              <div className="rounded-xl border border-[#3C2A12] bg-[linear-gradient(180deg,#23190D_0%,#17120C_100%)] p-4">
                <div className="text-xs uppercase tracking-[0.16em] text-[#D0A267]">
                  Total Price
                </div>
                <div className="mt-2 text-xl font-semibold text-white">
                  {Number(
                    selectedPurchaseAttempt.total_price ?? 0,
                  ).toLocaleString()}{" "}
                  XSEED
                </div>
              </div>
            </div>

            <div className="space-y-3 rounded-xl border border-white/5 bg-[#15171C] p-4">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                  Attempt ID
                </div>
                <div className="mt-1 break-all text-white">
                  {selectedPurchaseAttempt.attempt_id}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                  Transaction Hash
                </div>
                <div className="mt-1 break-all text-white">
                  {selectedPurchaseAttempt.tx_hash ? (
                    <a
                      href={`${SNOWTRACE_TX_BASE_URL}${selectedPurchaseAttempt.tx_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#8AB4FF] transition-colors hover:text-white"
                    >
                      <span>{selectedPurchaseAttempt.tx_hash}</span>
                      <ExternalLink className="h-4 w-4 flex-shrink-0" />
                    </a>
                  ) : (
                    "Not submitted"
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                  Receiver
                </div>
                <div className="mt-1 break-all text-white">
                  {selectedPurchaseAttempt.receiver_address}
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                    Created
                  </div>
                  <div className="mt-1 text-white">
                    {formatAttemptDate(selectedPurchaseAttempt.created_at)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                    Submitted
                  </div>
                  <div className="mt-1 text-white">
                    {formatAttemptDate(selectedPurchaseAttempt.submitted_at)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                    Confirmed
                  </div>
                  <div className="mt-1 text-white">
                    {formatAttemptDate(selectedPurchaseAttempt.confirmed_at)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-[#7D8490]">
                    Recorded
                  </div>
                  <div className="mt-1 text-white">
                    {formatAttemptDate(selectedPurchaseAttempt.db_recorded_at)}
                  </div>
                </div>
              </div>
              {selectedPurchaseAttempt.error_message && (
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-red-300">
                    Error
                  </div>
                  <div className="mt-1 text-red-200">
                    {selectedPurchaseAttempt.error_message}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
export default GamifiedRaffle;
